import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Egg,
  Info,
  Lock,
  ShieldCheck,
  BriefcaseBusiness,
  BadgeDollarSign,
  type LucideIcon,
} from "lucide-react-native";
import {
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogHeader,
  DialogIcon,
  IconButton,
  EmptyState,
  PrimaryButton,
  SectionTitle,
  Tag,
  CardSurface,
  cardSurface,
} from "@louise/moon-ui";
import { DateField } from "@louise/moon-ui/primitives/DateField";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import type { PlanAction } from "../plan.types";
import { getJourneyStepCompletionId } from "../completion";
import {
  doyleAgeRange,
  doyleLiveBirthEstimate,
  projectFertilityPrediction,
  type ObservedValue,
  type RateEstimate,
} from "@louise/fertility-projections";
import { convertAmhToNgMl } from "../../utils/eggSuccessCalculator";
import { useAfcResults, useAmhResults } from "../../utils/ovarianReserveInputs";
import type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";
import { AfcSourceSheet, type AfcSelection } from "./AfcSourceSheet";
import { AmhSourceSheet, type AmhSelection } from "./AmhSourceSheet";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";
import {
  BiologyIllustration,
  type BiologyVisualKind,
} from "./EggFreezingBiologyCarouselCard";
import { NeutralStepPill } from "./NeutralStepPill";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import {
  OutcomeDotFunnelRows,
  type OutcomeDotFunnelStage,
} from "./OutcomeDotFunnelRows";
import { InlineTaskSectionCard, TaskSectionCard } from "./TaskSectionCard";
import { TimelineSubtaskCard } from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";

const blobGrainTexture =
  require("@src/apps/moon/assets/images/gradient-grain-texture-2.jpg") as number;

const DATE_OF_BIRTH_DIALOG_ILLUSTRATION =
  require("@src/apps/moon/assets/images/date-of-birth-dialog-illustration.png") as number;

type IllustrationVariant = "flat" | "organic" | "organic-explanatory";

type BlobTone = "cream" | "amber" | "copper" | "brown" | "frost";

type BlobShape = {
  x: number;
  y: number;
  w: number;
  h: number;
  tone: BlobTone;
  opacity?: number;
  rotate?: number;
};

type BlobMarker = {
  x: number;
  y: number;
  size: number;
  tone: BlobTone;
  opacity?: number;
};

const BLOB_TONES: Record<BlobTone, [string, string, string]> = {
  cream: ["#FFF8EC", "#F1D1A6", "#B9784C"],
  amber: ["#FFE8A5", "#F4B34F", "#9C5528"],
  copper: ["#F2B387", "#B76545", "#542F27"],
  brown: ["#9C604D", "#5B332C", "#2F1D19"],
  frost: ["#FFFDFC", "#F3E5CF", "#B88C68"],
};

const BLOB_SCENES: Record<
  BiologyVisualKind,
  { blobs: BlobShape[]; markers: BlobMarker[]; pulse?: boolean }
> = {
  "normal-cycle": {
    blobs: [
      { x: 28, y: 34, w: 92, h: 58, tone: "cream", rotate: -8 },
      { x: 68, y: 18, w: 72, h: 76, tone: "copper", opacity: 0.9, rotate: 10 },
      { x: 108, y: 48, w: 40, h: 38, tone: "amber", rotate: -12 },
    ],
    markers: [
      { x: 50, y: 68, size: 10, tone: "cream" },
      { x: 72, y: 52, size: 12, tone: "frost" },
      { x: 100, y: 63, size: 24, tone: "amber" },
    ],
  },
  "follicle-cohort": {
    blobs: [
      { x: 24, y: 32, w: 118, h: 62, tone: "cream", rotate: 4 },
      { x: 54, y: 18, w: 78, h: 78, tone: "amber", opacity: 0.85, rotate: -8 },
      { x: 96, y: 45, w: 48, h: 42, tone: "copper", opacity: 0.8 },
    ],
    markers: [
      { x: 48, y: 68, size: 15, tone: "cream" },
      { x: 73, y: 50, size: 19, tone: "amber" },
      { x: 99, y: 70, size: 16, tone: "frost" },
      { x: 125, y: 54, size: 13, tone: "copper" },
    ],
  },
  "medication-signal": {
    pulse: true,
    blobs: [
      { x: 25, y: 56, w: 58, h: 28, tone: "copper", rotate: -16 },
      { x: 58, y: 34, w: 62, h: 30, tone: "amber", rotate: 10 },
      { x: 94, y: 61, w: 58, h: 28, tone: "cream", rotate: -12 },
      { x: 118, y: 36, w: 32, h: 28, tone: "amber", opacity: 0.82 },
    ],
    markers: [
      { x: 46, y: 66, size: 8, tone: "frost" },
      { x: 73, y: 47, size: 10, tone: "amber" },
      { x: 98, y: 72, size: 8, tone: "cream" },
      { x: 132, y: 49, size: 8, tone: "frost" },
    ],
  },
  monitoring: {
    pulse: true,
    blobs: [
      { x: 28, y: 64, w: 34, h: 24, tone: "brown", opacity: 0.66 },
      { x: 55, y: 50, w: 44, h: 30, tone: "copper", rotate: -10 },
      { x: 88, y: 58, w: 42, h: 28, tone: "cream", rotate: 7 },
      { x: 114, y: 32, w: 42, h: 34, tone: "amber", rotate: -8 },
    ],
    markers: [
      { x: 47, y: 77, size: 5, tone: "frost" },
      { x: 72, y: 62, size: 7, tone: "amber" },
      { x: 99, y: 68, size: 6, tone: "cream" },
      { x: 130, y: 42, size: 7, tone: "frost" },
    ],
  },
  trigger: {
    pulse: true,
    blobs: [
      { x: 50, y: 22, w: 78, h: 74, tone: "amber", rotate: 8 },
      { x: 67, y: 38, w: 44, h: 40, tone: "frost", opacity: 0.7 },
      { x: 112, y: 30, w: 32, h: 34, tone: "copper", opacity: 0.8, rotate: 18 },
    ],
    markers: [{ x: 86, y: 61, size: 14, tone: "brown", opacity: 0.7 }],
  },
  retrieval: {
    pulse: true,
    blobs: [
      { x: 34, y: 42, w: 72, h: 54, tone: "cream", rotate: -6 },
      { x: 51, y: 34, w: 42, h: 42, tone: "amber" },
      { x: 86, y: 63, w: 34, h: 30, tone: "copper", rotate: 12 },
      {
        x: 118,
        y: 26,
        w: 40,
        h: 18,
        tone: "brown",
        opacity: 0.55,
        rotate: -28,
      },
    ],
    markers: [
      { x: 68, y: 59, size: 16, tone: "frost" },
      { x: 91, y: 73, size: 12, tone: "cream" },
    ],
  },
  reserve: {
    blobs: [
      { x: 32, y: 42, w: 110, h: 48, tone: "cream", rotate: -4 },
      { x: 39, y: 58, w: 30, h: 24, tone: "amber" },
      { x: 66, y: 43, w: 27, h: 24, tone: "copper" },
      { x: 92, y: 58, w: 24, h: 20, tone: "frost" },
      { x: 118, y: 49, w: 20, h: 18, tone: "brown", opacity: 0.55 },
    ],
    markers: [
      { x: 48, y: 68, size: 8, tone: "amber" },
      { x: 63, y: 55, size: 7, tone: "cream" },
      { x: 80, y: 49, size: 6, tone: "frost" },
      { x: 103, y: 65, size: 5, tone: "copper" },
      { x: 125, y: 58, size: 4, tone: "brown" },
    ],
  },
  quality: {
    pulse: true,
    blobs: [
      { x: 52, y: 28, w: 70, h: 66, tone: "frost", rotate: -4 },
      { x: 70, y: 43, w: 36, h: 34, tone: "cream", opacity: 0.78 },
      { x: 110, y: 28, w: 24, h: 24, tone: "copper", opacity: 0.72 },
    ],
    markers: [
      { x: 68, y: 39, size: 5, tone: "brown", opacity: 0.52 },
      { x: 93, y: 73, size: 4, tone: "copper", opacity: 0.48 },
      { x: 120, y: 52, size: 4, tone: "brown", opacity: 0.44 },
    ],
  },
  freezing: {
    pulse: true,
    blobs: [
      { x: 52, y: 24, w: 74, h: 70, tone: "frost", rotate: -5 },
      { x: 64, y: 38, w: 48, h: 42, tone: "cream", opacity: 0.78 },
      { x: 110, y: 36, w: 32, h: 32, tone: "amber", opacity: 0.58 },
    ],
    markers: [
      { x: 86, y: 61, size: 18, tone: "frost" },
      { x: 68, y: 43, size: 5, tone: "cream", opacity: 0.75 },
      { x: 118, y: 79, size: 5, tone: "cream", opacity: 0.75 },
    ],
  },
  maturity: {
    pulse: true,
    blobs: [
      { x: 28, y: 40, w: 112, h: 58, tone: "cream", rotate: 5 },
      { x: 58, y: 26, w: 48, h: 44, tone: "copper", rotate: -9 },
      { x: 86, y: 54, w: 54, h: 44, tone: "amber", rotate: 11 },
    ],
    markers: [
      { x: 55, y: 68, size: 12, tone: "cream" },
      { x: 79, y: 48, size: 15, tone: "frost" },
      { x: 105, y: 70, size: 18, tone: "amber" },
      { x: 128, y: 52, size: 10, tone: "copper" },
    ],
  },
  attrition: {
    blobs: [
      { x: 22, y: 30, w: 78, h: 32, tone: "cream", rotate: -4 },
      { x: 33, y: 68, w: 62, h: 34, tone: "copper", rotate: 7 },
      { x: 104, y: 42, w: 48, h: 48, tone: "amber", rotate: -9 },
    ],
    markers: [
      { x: 33, y: 39, size: 7, tone: "cream" },
      { x: 57, y: 39, size: 7, tone: "frost" },
      { x: 80, y: 39, size: 7, tone: "cream" },
      { x: 51, y: 73, size: 11, tone: "copper" },
      { x: 75, y: 73, size: 10, tone: "cream" },
      { x: 122, y: 62, size: 18, tone: "amber" },
    ],
  },
};

function AnimatedBlob({
  blob,
  motion,
  index,
}: {
  blob: BlobShape;
  motion: Animated.Value;
  index: number;
}) {
  const translateY = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [index % 2 === 0 ? -2 : 2, index % 2 === 0 ? 3 : -3],
  });
  const scale = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [0.99, 1.035],
  });
  const rotate = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [
      `${(blob.rotate ?? 0) - 2}deg`,
      `${(blob.rotate ?? 0) + 2}deg`,
    ],
  });

  return (
    <Animated.View
      style={{
        position: "absolute",
        left: blob.x,
        top: blob.y,
        width: blob.w,
        height: blob.h,
        opacity: blob.opacity ?? 1,
        borderRadius: Math.max(blob.w, blob.h),
        overflow: "hidden",
        transform: [{ translateY }, { scale }, { rotate }],
      }}
    >
      <LinearGradient
        colors={BLOB_TONES[blob.tone]}
        start={{ x: 0.15, y: 0.08 }}
        end={{ x: 0.85, y: 0.94 }}
        style={{
          flex: 1,
          borderRadius: Math.max(blob.w, blob.h),
          shadowColor: "#4A302C",
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.12,
          shadowRadius: 18,
        }}
      />
      <Image
        source={blobGrainTexture}
        resizeMode="cover"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          opacity: 0.16,
        }}
      />
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: blob.w * 0.24,
          top: blob.h * 0.18,
          width: blob.w * 0.26,
          height: blob.h * 0.22,
          borderRadius: Math.max(blob.w, blob.h),
          backgroundColor: "rgba(255,255,255,0.46)",
          opacity: 0.72,
        }}
      />
    </Animated.View>
  );
}

function BlobMarkerView({
  marker,
  motion,
  index,
}: {
  marker: BlobMarker;
  motion: Animated.Value;
  index: number;
}) {
  const translateY = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [index % 2 === 0 ? 1 : -1, index % 2 === 0 ? -2 : 2],
  });

  return (
    <Animated.View
      style={{
        position: "absolute",
        left: marker.x,
        top: marker.y,
        width: marker.size,
        height: marker.size,
        opacity: marker.opacity ?? 1,
        transform: [{ translateY }],
      }}
    >
      <LinearGradient
        colors={BLOB_TONES[marker.tone]}
        start={{ x: 0.2, y: 0.1 }}
        end={{ x: 0.8, y: 1 }}
        style={{
          flex: 1,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.34)",
        }}
      />
    </Animated.View>
  );
}

function OrganicDot({
  x,
  y,
  size,
  tone,
  opacity,
  borderStyle,
}: {
  x: number;
  y: number;
  size: number;
  tone: BlobTone;
  opacity?: number;
  borderStyle?: "solid" | "dashed";
}) {
  return (
    <View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        borderRadius: 999,
        overflow: "hidden",
        opacity: opacity ?? 1,
        borderWidth: borderStyle ? 1 : 0,
        borderStyle,
        borderColor: "rgba(92,52,32,0.24)",
      }}
    >
      <LinearGradient
        colors={BLOB_TONES[tone]}
        start={{ x: 0.18, y: 0.08 }}
        end={{ x: 0.86, y: 0.94 }}
        style={{ flex: 1 }}
      />
      <Image
        source={blobGrainTexture}
        resizeMode="cover"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          opacity: 0.14,
        }}
      />
    </View>
  );
}

function OrganicNeedle() {
  return (
    <View
      style={{
        position: "absolute",
        left: 96,
        top: 30,
        width: 70,
        height: 2,
        borderRadius: 999,
        backgroundColor: "rgba(92,52,32,0.58)",
        transform: [{ rotate: "-24deg" }],
      }}
    >
      <View
        style={{
          position: "absolute",
          right: -5,
          top: -3,
          width: 12,
          height: 8,
          borderRadius: 6,
          backgroundColor: "rgba(255,248,236,0.76)",
          borderWidth: 1,
          borderColor: "rgba(92,52,32,0.16)",
        }}
      />
    </View>
  );
}

function OrganicMeasurementLine({
  left,
  top,
  width,
  rotate = 0,
}: {
  left: number;
  top: number;
  width: number;
  rotate?: number;
}) {
  return (
    <View
      style={{
        position: "absolute",
        left,
        top,
        width,
        height: 1,
        backgroundColor: "rgba(92,52,32,0.42)",
        transform: [{ rotate: `${rotate}deg` }],
      }}
    >
      <View
        style={{
          position: "absolute",
          left: 0,
          top: -3,
          width: 1,
          height: 7,
          backgroundColor: "rgba(92,52,32,0.42)",
        }}
      />
      <View
        style={{
          position: "absolute",
          right: 0,
          top: -3,
          width: 1,
          height: 7,
          backgroundColor: "rgba(92,52,32,0.42)",
        }}
      />
    </View>
  );
}

function OrganicPulseRing({
  x,
  y,
  size,
  opacity = 0.22,
}: {
  x: number;
  y: number;
  size: number;
  opacity?: number;
}) {
  return (
    <View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: size,
        height: size,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: `rgba(156,85,40,${opacity})`,
      }}
    />
  );
}

export function OrganicExplanatoryIllustration({
  kind,
}: {
  kind: BiologyVisualKind;
}) {
  const reducedMotion = useReducedMotion();
  const motion = useRef(new Animated.Value(0)).current;
  const scene = BLOB_SCENES[kind];

  useEffect(() => {
    motion.setValue(0);
    if (reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(motion, {
          toValue: 1,
          duration: scene.pulse ? 2200 : 3800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(motion, {
          toValue: 0,
          duration: scene.pulse ? 2200 : 3800,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [motion, scene.pulse, reducedMotion]);

  const pulseScale = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [0.98, 1.04],
  });
  const pulseOpacity = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [0.24, 0.46],
  });
  const drift = motion.interpolate({
    inputRange: [0, 1],
    outputRange: [-3, 3],
  });

  return (
    <View
      style={{
        alignSelf: "center",
        width: 174,
        height: 118,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 6,
        marginBottom: 12,
        overflow: "visible",
      }}
    >
      {kind === "reserve" ? (
        <View style={{ width: 174, height: 118 }}>
          <View
            style={{
              position: "absolute",
              left: 24,
              top: 17,
              right: 20,
              bottom: 24,
            }}
          >
            {(
              [
                [0, 0],
                [10, 4],
                [20, 9],
                [30, 15],
                [42, 23],
                [55, 33],
                [68, 43],
                [82, 55],
                [96, 64],
                [110, 71],
              ] satisfies [number, number][]
            ).map(([x, y], index) => (
              <OrganicDot
                key={`${x}-${y}`}
                x={x}
                y={y}
                size={Math.max(4, 12 - index * 0.8)}
                tone={
                  index < 3
                    ? "cream"
                    : index < 6
                      ? "amber"
                      : index < 8
                        ? "copper"
                        : "brown"
                }
                opacity={Math.max(0.34, 0.96 - index * 0.07)}
              />
            ))}
            {(
              [
                [5, 18],
                [18, 23],
                [34, 31],
                [47, 41],
                [61, 52],
                [78, 62],
                [96, 70],
              ] satisfies [number, number][]
            ).map(([x, y], index) => (
              <OrganicDot
                key={`small-${x}-${y}`}
                x={x}
                y={y}
                size={Math.max(3, 7 - index * 0.45)}
                tone={index < 3 ? "frost" : index < 5 ? "amber" : "copper"}
                opacity={Math.max(0.24, 0.72 - index * 0.07)}
              />
            ))}
          </View>
        </View>
      ) : kind === "quality" ? (
        <View style={{ width: 174, height: 118 }}>
          <Animated.View
            style={{
              position: "absolute",
              left: 52,
              top: 24,
              width: 72,
              height: 72,
              transform: [{ scale: pulseScale }],
            }}
          >
            <OrganicPulseRing x={-8} y={-8} size={88} opacity={0.14} />
            <OrganicDot x={10} y={10} size={48} tone="frost" />
            <OrganicDot x={27} y={27} size={14} tone="cream" opacity={0.92} />
          </Animated.View>
          {[0, 1, 2].map((index) => (
            <View
              key={index}
              style={{
                position: "absolute",
                left: 57 + index * 31,
                top: 32 + index * 18,
                width: 14,
                height: 1,
                backgroundColor: "rgba(92,52,32,0.26)",
                transform: [{ rotate: `${index % 2 === 0 ? -22 : 18}deg` }],
              }}
            />
          ))}
          <OrganicDot x={125} y={30} size={10} tone="copper" opacity={0.58} />
          <OrganicDot x={42} y={78} size={8} tone="brown" opacity={0.4} />
        </View>
      ) : kind === "normal-cycle" ? (
        <View style={{ width: 174, height: 118 }}>
          <Animated.View
            style={{
              position: "absolute",
              left: 28,
              top: 42,
              transform: [{ translateY: drift }],
            }}
          >
            <OrganicDot x={0} y={8} size={16} tone="cream" opacity={0.72} />
            <OrganicDot x={24} y={2} size={19} tone="frost" opacity={0.78} />
            <OrganicDot x={50} y={10} size={18} tone="cream" opacity={0.74} />
            <OrganicDot x={79} y={0} size={34} tone="amber" />
          </Animated.View>
          <OrganicPulseRing x={101} y={39} size={42} />
          <OrganicMeasurementLine left={102} top={91} width={42} />
        </View>
      ) : kind === "follicle-cohort" || kind === "maturity" ? (
        <View style={{ width: 174, height: 118 }}>
          <Animated.View
            style={{
              position: "absolute",
              left: 22,
              top: 22,
              transform: [{ translateY: drift }],
            }}
          >
            <OrganicDot x={0} y={42} size={16} tone="cream" />
            <OrganicDot x={24} y={29} size={22} tone="frost" />
            <OrganicDot x={54} y={20} size={30} tone="amber" />
            <OrganicDot x={91} y={31} size={24} tone="copper" />
            <OrganicDot x={120} y={46} size={15} tone="brown" opacity={0.68} />
          </Animated.View>
          {kind === "maturity" ? (
            <>
              <OrganicMeasurementLine left={79} top={82} width={31} />
              <OrganicMeasurementLine left={114} top={92} width={24} />
            </>
          ) : null}
        </View>
      ) : kind === "medication-signal" ? (
        <View style={{ width: 174, height: 118 }}>
          {[0, 1, 2].map((index) => (
            <Animated.View
              key={index}
              style={{
                position: "absolute",
                left: 27 + index * 39,
                top: 24 + index * 11,
                transform: [{ translateY: drift }],
              }}
            >
              <OrganicDot x={0} y={33} size={23} tone="cream" />
              <OrganicPulseRing x={-8} y={25} size={39} opacity={0.18} />
              <OrganicPulseRing x={-15} y={18} size={53} opacity={0.12} />
            </Animated.View>
          ))}
        </View>
      ) : kind === "monitoring" ? (
        <View style={{ width: 174, height: 118 }}>
          <OrganicDot x={34} y={68} size={18} tone="brown" opacity={0.64} />
          <OrganicDot x={62} y={54} size={26} tone="copper" />
          <OrganicDot x={97} y={48} size={32} tone="amber" />
          <OrganicDot x={134} y={66} size={16} tone="cream" />
          <OrganicMeasurementLine left={95} top={41} width={35} rotate={-8} />
          <OrganicMeasurementLine left={60} top={49} width={28} rotate={-7} />
          <View
            style={{
              position: "absolute",
              left: 33,
              top: 24,
              width: 112,
              height: 70,
              borderRadius: 18,
              borderWidth: 1,
              borderColor: "rgba(92,52,32,0.12)",
            }}
          />
        </View>
      ) : kind === "trigger" ? (
        <View style={{ width: 174, height: 118 }}>
          <Animated.View
            style={{
              position: "absolute",
              left: 53,
              top: 18,
              width: 70,
              height: 70,
              transform: [{ scale: pulseScale }],
            }}
          >
            <OrganicPulseRing x={-10} y={-10} size={90} opacity={0.16} />
            <OrganicPulseRing x={2} y={2} size={66} opacity={0.22} />
            <OrganicDot x={12} y={12} size={46} tone="amber" />
            <OrganicDot x={29} y={29} size={13} tone="frost" opacity={0.9} />
          </Animated.View>
          <OrganicMeasurementLine left={61} top={96} width={54} />
        </View>
      ) : kind === "retrieval" ? (
        <View style={{ width: 174, height: 118 }}>
          <OrganicDot x={39} y={39} size={56} tone="cream" />
          <OrganicDot x={59} y={58} size={18} tone="frost" />
          <OrganicDot x={84} y={74} size={12} tone="amber" opacity={0.82} />
          <OrganicNeedle />
          <Animated.View
            style={{
              position: "absolute",
              left: 123,
              top: 57,
              opacity: pulseOpacity,
            }}
          >
            <OrganicDot x={0} y={0} size={11} tone="frost" />
          </Animated.View>
        </View>
      ) : kind === "freezing" ? (
        <View style={{ width: 174, height: 118 }}>
          <OrganicDot x={66} y={34} size={46} tone="frost" />
          <OrganicDot x={82} y={50} size={14} tone="cream" opacity={0.92} />
          {[0, 1, 2].map((index) => (
            <View
              key={index}
              style={{
                position: "absolute",
                left: 36 + index * 45,
                top: 75 - index * 18,
                width: 28,
                height: 1,
                backgroundColor: "rgba(255,255,255,0.86)",
                transform: [{ rotate: `${index * 28 - 20}deg` }],
              }}
            />
          ))}
          <OrganicPulseRing x={57} y={25} size={64} opacity={0.18} />
        </View>
      ) : kind === "attrition" ? (
        <View style={{ width: 174, height: 118 }}>
          {[0, 1, 2, 3, 4].map((index) => (
            <OrganicDot
              key={`top-${index}`}
              x={27 + index * 23}
              y={28}
              size={12}
              tone={index % 2 === 0 ? "cream" : "frost"}
            />
          ))}
          {[0, 1, 2].map((index) => (
            <OrganicDot
              key={`mid-${index}`}
              x={50 + index * 28}
              y={58}
              size={15}
              tone={index === 1 ? "amber" : "copper"}
            />
          ))}
          <OrganicDot x={78} y={88} size={20} tone="amber" />
          <View
            style={{
              position: "absolute",
              left: 38,
              top: 45,
              width: 100,
              height: 42,
              borderLeftWidth: 1,
              borderRightWidth: 1,
              borderColor: "rgba(92,52,32,0.18)",
              transform: [{ perspective: 80 }, { rotateX: "18deg" }],
            }}
          />
        </View>
      ) : (
        <View style={{ width: 174, height: 118 }}>
          {scene.blobs.map((blob, index) => (
            <AnimatedBlob
              key={`${blob.x}-${blob.y}-${index}`}
              blob={blob}
              motion={motion}
              index={index}
            />
          ))}
          {scene.markers.map((marker, index) => (
            <BlobMarkerView
              key={`${marker.x}-${marker.y}-${index}`}
              marker={marker}
              motion={motion}
              index={index}
            />
          ))}
        </View>
      )}
    </View>
  );
}

function OrganicBiologyIllustration({ kind }: { kind: BiologyVisualKind }) {
  const reducedMotion = useReducedMotion();
  const motion = useRef(new Animated.Value(0)).current;
  const scene = BLOB_SCENES[kind];

  useEffect(() => {
    motion.setValue(0);
    if (reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(motion, {
          toValue: 1,
          duration: scene.pulse ? 2400 : 4200,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(motion, {
          toValue: 0,
          duration: scene.pulse ? 2400 : 4200,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [motion, scene.pulse, reducedMotion]);

  return (
    <View
      style={{
        alignSelf: "center",
        width: 174,
        height: 118,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 6,
        marginBottom: 12,
        overflow: "visible",
      }}
    >
      <View
        style={{
          width: 174,
          height: 118,
          overflow: "visible",
        }}
      >
        {scene.blobs.map((blob, index) => (
          <AnimatedBlob
            key={`${blob.x}-${blob.y}-${index}`}
            blob={blob}
            motion={motion}
            index={index}
          />
        ))}
        {scene.markers.map((marker, index) => (
          <BlobMarkerView
            key={`${marker.x}-${marker.y}-${index}`}
            marker={marker}
            motion={motion}
            index={index}
          />
        ))}
      </View>
    </View>
  );
}

export type DeepBiologySlide = {
  id: string;
  visual: BiologyVisualKind;
  label: string;
  title: string;
  body: string;
  calloutLabel: string;
  callout: string;
  cta?: PlanAction;
  detail: {
    title: string;
    sections: { title: string; body: string }[];
  };
};

export function buildEggFreezingBiologySlides(
  t: TranslationFunction,
): DeepBiologySlide[] {
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  return [
    {
      id: "egg-supply",
      visual: "reserve",
      label: t(`${ns}.egg_supply_label`),
      title: t(`${ns}.egg_supply_title`),
      body: t(`${ns}.egg_supply_body`),
      calloutLabel: t(`${ns}.egg_supply_callout_label`),
      callout: t(`${ns}.egg_supply_callout`),
      cta: {
        label: t(`${ns}.egg_supply_cta`),
        screen: "AtHomeFertilityTesting",
      },
      detail: {
        title: t(`${ns}.egg_supply_detail_title`),
        sections: [
          {
            title: t(`${ns}.egg_supply_s1_title`),
            body: t(`${ns}.egg_supply_s1_body`),
          },
          {
            title: t(`${ns}.egg_supply_s2_title`),
            body: t(`${ns}.egg_supply_s2_body`),
          },
          {
            title: t(`${ns}.egg_supply_s3_title`),
            body: t(`${ns}.egg_supply_s3_body`),
          },
        ],
      },
    },
    {
      id: "egg-quality",
      visual: "quality",
      label: t(`${ns}.egg_quality_label`),
      title: t(`${ns}.egg_quality_title`),
      body: t(`${ns}.egg_quality_body`),
      calloutLabel: t(`${ns}.egg_quality_callout_label`),
      callout: t(`${ns}.egg_quality_callout`),
      detail: {
        title: t(`${ns}.egg_quality_detail_title`),
        sections: [
          {
            title: t(`${ns}.egg_quality_s1_title`),
            body: t(`${ns}.egg_quality_s1_body`),
          },
          {
            title: t(`${ns}.egg_quality_s2_title`),
            body: t(`${ns}.egg_quality_s2_body`),
          },
          {
            title: t(`${ns}.egg_quality_s3_title`),
            body: t(`${ns}.egg_quality_s3_body`),
          },
          {
            title: t(`${ns}.egg_quality_s4_title`),
            body: t(`${ns}.egg_quality_s4_body`),
          },
          {
            title: t(`${ns}.egg_quality_s5_title`),
            body: t(`${ns}.egg_quality_s5_body`),
          },
        ],
      },
    },
    {
      id: "ovary-follicle-egg",
      visual: "follicle-cohort",
      label: t(`${ns}.ovaries_follicles_label`),
      title: t(`${ns}.ovaries_follicles_title`),
      body: t(`${ns}.ovaries_follicles_body`),
      calloutLabel: t(`${ns}.key_distinction_label`),
      callout: t(`${ns}.ovaries_follicles_callout`),
      detail: {
        title: t(`${ns}.ovaries_follicles_detail_title`),
        sections: [
          {
            title: t(`${ns}.ovaries_follicles_s1_title`),
            body: t(`${ns}.ovaries_follicles_s1_body`),
          },
          {
            title: t(`${ns}.ovaries_follicles_s2_title`),
            body: t(`${ns}.ovaries_follicles_s2_body`),
          },
          {
            title: t(`${ns}.ovaries_follicles_s3_title`),
            body: t(`${ns}.ovaries_follicles_s3_body`),
          },
          {
            title: t(`${ns}.ovaries_follicles_s4_title`),
            body: t(`${ns}.ovaries_follicles_s4_body`),
          },
        ],
      },
    },
    {
      id: "normal-selection",
      visual: "normal-cycle",
      label: t(`${ns}.normal_cycle_label`),
      title: t(`${ns}.normal_cycle_title`),
      body: t(`${ns}.normal_cycle_body`),
      calloutLabel: t(`${ns}.people_often_get_wrong_label`),
      callout: t(`${ns}.normal_cycle_callout`),
      detail: {
        title: t(`${ns}.normal_cycle_detail_title`),
        sections: [
          {
            title: t(`${ns}.normal_cycle_s1_title`),
            body: t(`${ns}.normal_cycle_s1_body`),
          },
          {
            title: t(`${ns}.normal_cycle_s2_title`),
            body: t(`${ns}.normal_cycle_s2_body`),
          },
          {
            title: t(`${ns}.normal_cycle_s3_title`),
            body: t(`${ns}.normal_cycle_s3_body`),
          },
          {
            title: t(`${ns}.normal_cycle_s4_title`),
            body: t(`${ns}.normal_cycle_s4_body`),
          },
        ],
      },
    },
    {
      id: "stimulation-cohort",
      visual: "follicle-cohort",
      label: t("homepage.shared.stimulation"),
      title: t(`${ns}.stimulation_title`),
      body: t(`${ns}.stimulation_body`),
      calloutLabel: t(`${ns}.what_matters_here_label`),
      callout: t(`${ns}.stimulation_callout`),
      detail: {
        title: t(`${ns}.stimulation_detail_title`),
        sections: [
          {
            title: t(`${ns}.stimulation_s1_title`),
            body: t(`${ns}.stimulation_s1_body`),
          },
          {
            title: t(`${ns}.stimulation_s2_title`),
            body: t(`${ns}.stimulation_s2_body`),
          },
          {
            title: t(`${ns}.stimulation_s3_title`),
            body: t(`${ns}.stimulation_s3_body`),
          },
        ],
      },
    },
    {
      id: "follicle-egg-maturity",
      visual: "maturity",
      label: t(`${ns}.maturity_label`),
      title: t(`${ns}.maturity_title`),
      body: t(`${ns}.maturity_body`),
      calloutLabel: t(`${ns}.key_distinction_label`),
      callout: t(`${ns}.maturity_callout`),
      cta: {
        label: t(`${ns}.maturity_cta`),
        screen: "MedicalHistory",
      },
      detail: {
        title: t(`${ns}.maturity_detail_title`),
        sections: [
          {
            title: t(`${ns}.maturity_s1_title`),
            body: t(`${ns}.maturity_s1_body`),
          },
          {
            title: t(`${ns}.maturity_s2_title`),
            body: t(`${ns}.maturity_s2_body`),
          },
          {
            title: t(`${ns}.maturity_s3_title`),
            body: t(`${ns}.maturity_s3_body`),
          },
        ],
      },
    },
    {
      id: "monitoring",
      visual: "monitoring",
      label: t(`${ns}.monitoring_label`),
      title: t(`${ns}.monitoring_title`),
      body: t(`${ns}.monitoring_body`),
      calloutLabel: t(`${ns}.what_matters_here_label`),
      callout: t(`${ns}.monitoring_callout`),
      detail: {
        title: t(`${ns}.monitoring_detail_title`),
        sections: [
          {
            title: t(`${ns}.monitoring_s1_title`),
            body: t(`${ns}.monitoring_s1_body`),
          },
          {
            title: t(`${ns}.monitoring_s2_title`),
            body: t(`${ns}.monitoring_s2_body`),
          },
          {
            title: t(`${ns}.monitoring_s3_title`),
            body: t(`${ns}.monitoring_s3_body`),
          },
          {
            title: t(`${ns}.monitoring_s4_title`),
            body: t(`${ns}.monitoring_s4_body`),
          },
        ],
      },
    },
    {
      id: "trigger",
      visual: "trigger",
      label: t(`${ns}.trigger_label`),
      title: t(`${ns}.trigger_title`),
      body: t(`${ns}.trigger_body`),
      calloutLabel: t(`${ns}.people_often_get_wrong_label`),
      callout: t(`${ns}.trigger_callout`),
      detail: {
        title: t(`${ns}.trigger_detail_title`),
        sections: [
          {
            title: t(`${ns}.trigger_s1_title`),
            body: t(`${ns}.trigger_s1_body`),
          },
          {
            title: t(`${ns}.trigger_s2_title`),
            body: t(`${ns}.trigger_s2_body`),
          },
          {
            title: t(`${ns}.trigger_s3_title`),
            body: t(`${ns}.trigger_s3_body`),
          },
        ],
      },
    },
    {
      id: "retrieval-procedure",
      visual: "retrieval",
      label: t(`${ns}.retrieval_label`),
      title: t(`${ns}.retrieval_procedure_title`),
      body: t(`${ns}.retrieval_procedure_body`),
      calloutLabel: t(`${ns}.what_people_worry_label`),
      callout: t(`${ns}.retrieval_procedure_callout`),
      detail: {
        title: t(`${ns}.retrieval_procedure_detail_title`),
        sections: [
          {
            title: t(`${ns}.retrieval_procedure_s1_title`),
            body: t(`${ns}.retrieval_procedure_s1_body`),
          },
          {
            title: t(`${ns}.retrieval_procedure_s2_title`),
            body: t(`${ns}.retrieval_procedure_s2_body`),
          },
          {
            title: t(`${ns}.retrieval_procedure_s3_title`),
            body: t(`${ns}.retrieval_procedure_s3_body`),
          },
        ],
      },
    },
    {
      id: "follicle-fluid",
      visual: "retrieval",
      label: t(`${ns}.lab_handoff_label`),
      title: t(`${ns}.lab_handoff_title`),
      body: t(`${ns}.lab_handoff_body`),
      calloutLabel: t(`${ns}.key_distinction_label`),
      callout: t(`${ns}.lab_handoff_callout`),
      detail: {
        title: t(`${ns}.lab_handoff_detail_title`),
        sections: [
          {
            title: t(`${ns}.lab_handoff_s1_title`),
            body: t(`${ns}.lab_handoff_s1_body`),
          },
          {
            title: t(`${ns}.lab_handoff_s2_title`),
            body: t(`${ns}.lab_handoff_s2_body`),
          },
          {
            title: t(`${ns}.lab_handoff_s3_title`),
            body: t(`${ns}.lab_handoff_s3_body`),
          },
        ],
      },
    },
    {
      id: "retrieval-count",
      visual: "retrieval",
      label: t(`${ns}.egg_count_label`),
      title: t(`${ns}.egg_count_title`),
      body: t(`${ns}.egg_count_body`),
      calloutLabel: t(`${ns}.what_people_ask_label`),
      callout: t(`${ns}.egg_count_callout`),
      cta: {
        label: t(`${ns}.egg_count_cta`),
        screen: "LearnScreen",
      },
      detail: {
        title: t(`${ns}.egg_count_detail_title`),
        sections: [
          {
            title: t(`${ns}.egg_count_s1_title`),
            body: t(`${ns}.egg_count_s1_body`),
          },
          {
            title: t(`${ns}.egg_count_s2_title`),
            body: t(`${ns}.egg_count_s2_body`),
          },
          {
            title: t(`${ns}.egg_count_s3_title`),
            body: t(`${ns}.egg_count_s3_body`),
          },
        ],
      },
    },
    {
      id: "mii",
      visual: "freezing",
      label: t(`${ns}.mii_label`),
      title: t(`${ns}.mii_title`),
      body: t(`${ns}.mii_body`),
      calloutLabel: t(`${ns}.term_to_know_label`),
      callout: t(`${ns}.mii_callout`),
      cta: {
        label: t(`${ns}.mii_cta`),
        screen: "LearnScreen",
      },
      detail: {
        title: t(`${ns}.mii_detail_title`),
        sections: [
          { title: t(`${ns}.mii_s1_title`), body: t(`${ns}.mii_s1_body`) },
          { title: t(`${ns}.mii_s2_title`), body: t(`${ns}.mii_s2_body`) },
          { title: t(`${ns}.mii_s3_title`), body: t(`${ns}.mii_s3_body`) },
          { title: t(`${ns}.mii_s4_title`), body: t(`${ns}.mii_s4_body`) },
        ],
      },
    },
    {
      id: "attrition",
      visual: "attrition",
      label: t(`${ns}.later_use_label`),
      title: t(`${ns}.attrition_title`),
      body: t(`${ns}.attrition_body`),
      calloutLabel: t(`${ns}.why_estimates_exist_label`),
      callout: t(`${ns}.attrition_callout`),
      cta: {
        label: t(`${ns}.attrition_cta`),
        screen: "MedicalHistory",
      },
      detail: {
        title: t(`${ns}.attrition_detail_title`),
        sections: [
          {
            title: t(`${ns}.attrition_s1_title`),
            body: t(`${ns}.attrition_s1_body`),
          },
          {
            title: t(`${ns}.attrition_s2_title`),
            body: t(`${ns}.attrition_s2_body`),
          },
          {
            title: t(`${ns}.attrition_s3_title`),
            body: t(`${ns}.attrition_s3_body`),
          },
        ],
      },
    },
  ];
}

function useDeepBiologySlides(): DeepBiologySlide[] {
  const t = useTranslations();
  return buildEggFreezingBiologySlides(t);
}

function useEstimateForAge(userAge?: number) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  if (!userAge) {
    return {
      value: t(`${ns}.estimate_none_value`),
      body: t(`${ns}.estimate_none_body`),
    };
  }

  if (userAge < 35) {
    return {
      value: t("homepage.shared.often_10_20_eggs"),
      body: t(`${ns}.estimate_under_35_body`),
    };
  }

  if (userAge <= 37) {
    return {
      value: t(`${ns}.estimate_8_14`),
      body: t(`${ns}.estimate_35_37_body`),
    };
  }

  if (userAge <= 40) {
    return {
      value: t(`${ns}.estimate_5_10`),
      body: t(`${ns}.estimate_37_40_body`),
    };
  }

  return {
    value: t(`${ns}.estimate_clinic_specific`),
    body: t(`${ns}.estimate_over_40_body`),
  };
}

function matureEggTargetForChildren(children: number) {
  if (children <= 1) return 15;
  if (children === 2) return 28;
  return 42 + (children - 3) * 14;
}

function euploidEmbryoTargetForChildren(children: number) {
  return Math.max(1, children) * 2;
}

type FunnelAmhInput = {
  value: number;
  unit: "ng/mL" | "pmol/L";
  sourceId?: string;
};

function observedReserveInputs(
  amh: FunnelAmhInput | null,
  afc: { value: number; sourceId?: string } | null,
) {
  return {
    amhNgMl: amh
      ? {
          value: convertAmhToNgMl(amh.value, amh.unit),
          provenance: {
            sourceType: amh.sourceId
              ? ("laboratory_report" as const)
              : ("patient_reported" as const),
            sourceId: amh.sourceId,
          },
        }
      : undefined,
    antralFollicleCount:
      afc === null
        ? undefined
        : {
            value: afc.value,
            provenance: {
              sourceType: afc.sourceId
                ? ("laboratory_report" as const)
                : ("patient_reported" as const),
              sourceId: afc.sourceId,
            },
          },
  };
}

function useBuildEggEstimateFunnel(
  userAge?: number,
  amh: FunnelAmhInput | null = null,
  afc: { value: number; sourceId?: string } | null = null,
) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const observedAge: ObservedValue<number> | undefined = userAge
    ? {
        value: userAge,
        provenance: { sourceType: "patient_reported" },
      }
    : undefined;
  const projection = userAge
    ? projectFertilityPrediction({
        kind: "egg_freezing_cycle",
        patient: {
          ageAtRetrieval: observedAge,
          ...observedReserveInputs(amh, afc),
        },
        cycle: {},
        goal: { kind: "expected_stage_count", stage: "mature_oocytes" },
      })
    : null;
  const matureStage = projection?.stages.find(
    (stage) => stage.id === "mature_oocytes",
  );
  const outcomeProjection = matureStage
    ? projectFertilityPrediction({
        kind: "frozen_oocyte_cohort",
        patient: { ageAtRetrieval: observedAge },
        oocytes: {
          frozen: {
            value: Math.max(0, Math.round(matureStage.estimate.mean)),
            provenance: {
              sourceType: "model_input",
              sourceId: projection?.model.id,
              note: "Mature-oocyte estimate carried into the general future-use baseline.",
            },
          },
        },
        goal: { kind: "expected_stage_count", stage: "live_births" },
      })
    : null;
  const stageValue = (id: string): number => {
    const stage =
      projection?.stages.find((candidate) => candidate.id === id) ??
      outcomeProjection?.stages.find((candidate) => candidate.id === id);
    return stage ? Math.max(0, Math.round(stage.estimate.mean)) : 0;
  };
  const hasOutcomeStage = (id: string) =>
    Boolean(outcomeProjection?.stages.some((stage) => stage.id === id));
  const retrieved = stageValue("retrieved_oocytes");
  const mature = stageValue("mature_oocytes");
  const hasRetrievedEstimate = Boolean(
    projection?.stages.some((stage) => stage.id === "retrieved_oocytes"),
  );
  const hasMatureEstimate = Boolean(
    projection?.stages.some(
      (stage) => stage.id === "mature_oocytes" && stage.basis === "modeled",
    ),
  );
  const frozen = stageValue("frozen_oocytes");
  const thawed = stageValue("thawed_oocytes");
  const fertilized = stageValue("fertilized_oocytes");
  const blastocysts = stageValue("blastocysts");
  const euploids = stageValue("euploid_embryos");
  const liveBirthProbability =
    outcomeProjection?.probabilityAtLeastOneLiveBirth?.mean ?? null;
  const perUnitLiveBirthRate = derivePerUnitLiveBirthRate(
    outcomeProjection,
    frozen,
  );
  return {
    stages: [
      {
        id: "retrieved",
        label: t("homepage.shared.eggs_retrieved"),
        value: retrieved,
        detail: t(`${ns}.funnel_retrieved_detail`),
        missing: !hasRetrievedEstimate,
      },
      {
        id: "mature",
        label: t("homepage.shared.mature_eggs"),
        value: mature,
        detail: t(`${ns}.funnel_mature_detail`, {
          params: { diff: String(Math.max(0, retrieved - mature)) },
        }),
        missing: !hasMatureEstimate,
      },
      {
        id: "frozen",
        label: t(`${ns}.funnel_frozen_label`),
        value: frozen,
        detail: t(`${ns}.funnel_frozen_detail`),
        missing: !hasOutcomeStage("frozen_oocytes"),
      },
      {
        id: "thawed",
        label: t(`${ns}.funnel_thawed_label`),
        value: thawed,
        detail: t(`${ns}.funnel_thawed_detail`, {
          params: { diff: String(Math.max(0, frozen - thawed)) },
        }),
        missing: !hasOutcomeStage("thawed_oocytes"),
      },
      {
        id: "fertilized",
        label: t(`${ns}.funnel_fertilized_label`),
        value: fertilized,
        detail: t(`${ns}.funnel_fertilized_detail`, {
          params: { diff: String(Math.max(0, thawed - fertilized)) },
        }),
        missing: !hasOutcomeStage("fertilized_oocytes"),
      },
      {
        id: "blastocysts",
        label: t("homepage.shared.blastocysts"),
        value: blastocysts,
        detail: t(`${ns}.funnel_blastocysts_detail`, {
          params: { diff: String(Math.max(0, fertilized - blastocysts)) },
        }),
        missing: !hasOutcomeStage("blastocysts"),
      },
      {
        id: "euploids",
        label: t(`${ns}.funnel_euploids_label`),
        value: euploids,
        detail: t(`${ns}.funnel_euploids_detail`, {
          params: { diff: String(Math.max(0, blastocysts - euploids)) },
        }),
        missing: !hasOutcomeStage("euploid_embryos"),
      },
      {
        id: "birth",
        label: t(`${ns}.funnel_birth_label`),
        value:
          liveBirthProbability == null
            ? 0
            : Math.max(0, Math.round(euploids * liveBirthProbability)),
        displayValue:
          liveBirthProbability == null
            ? undefined
            : `${Math.round(liveBirthProbability * 100)}%`,
        detail: t(`${ns}.funnel_birth_detail`),
        missing: liveBirthProbability == null,
        hideDots: true,
        progressValue: liveBirthProbability ?? undefined,
      },
    ],
    perUnitLiveBirthRate,
  };
}

function useBuildEmbryoEstimateFunnel(
  userAge?: number,
  amh: FunnelAmhInput | null = null,
  afc: { value: number; sourceId?: string } | null = null,
) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const observedAge: ObservedValue<number> | undefined = userAge
    ? {
        value: userAge,
        provenance: { sourceType: "patient_reported" },
      }
    : undefined;
  const projection = userAge
    ? projectFertilityPrediction({
        kind: "egg_freezing_cycle",
        patient: {
          ageAtRetrieval: observedAge,
          ...observedReserveInputs(amh, afc),
        },
        cycle: {},
        goal: { kind: "expected_stage_count", stage: "mature_oocytes" },
      })
    : null;
  const matureStage = projection?.stages.find(
    (stage) => stage.id === "mature_oocytes",
  );
  const outcomeProjection = matureStage
    ? projectFertilityPrediction({
        kind: "frozen_oocyte_cohort",
        patient: { ageAtRetrieval: observedAge },
        oocytes: {
          frozen: {
            value: Math.max(0, Math.round(matureStage.estimate.mean)),
            provenance: {
              sourceType: "model_input",
              sourceId: projection?.model.id,
              note: "Mature-oocyte estimate carried into the general future-use baseline.",
            },
          },
        },
        goal: { kind: "expected_stage_count", stage: "live_births" },
      })
    : null;
  const stageValue = (id: string): number => {
    const stage =
      projection?.stages.find((candidate) => candidate.id === id) ??
      outcomeProjection?.stages.find((candidate) => candidate.id === id);
    return stage ? Math.max(0, Math.round(stage.estimate.mean)) : 0;
  };
  const hasOutcomeStage = (id: string) =>
    Boolean(outcomeProjection?.stages.some((stage) => stage.id === id));
  const retrieved = stageValue("retrieved_oocytes");
  const mature = stageValue("mature_oocytes");
  const hasRetrievedEstimate = Boolean(
    projection?.stages.some((stage) => stage.id === "retrieved_oocytes"),
  );
  const hasMatureEstimate = Boolean(
    projection?.stages.some(
      (stage) => stage.id === "mature_oocytes" && stage.basis === "modeled",
    ),
  );
  const thawed = stageValue("thawed_oocytes");
  const fertilized = stageValue("fertilized_oocytes");
  const blastocysts = stageValue("blastocysts");
  const euploids = stageValue("euploid_embryos");
  const liveBirthProbability =
    outcomeProjection?.probabilityAtLeastOneLiveBirth?.mean ?? null;
  const perUnitLiveBirthRate = derivePerUnitLiveBirthRate(
    outcomeProjection,
    euploids,
  );
  return {
    stages: [
      {
        id: "retrieved",
        label: t("homepage.shared.eggs_retrieved"),
        value: retrieved,
        detail: t(`${ns}.embryo_funnel_retrieved_detail`),
        missing: !hasRetrievedEstimate,
      },
      {
        id: "mature",
        label: t("homepage.shared.mature_eggs"),
        value: mature,
        detail: t(`${ns}.embryo_funnel_mature_detail`, {
          params: { diff: String(Math.max(0, retrieved - mature)) },
        }),
        missing: !hasMatureEstimate,
      },
      {
        id: "fertilized",
        label: t(`${ns}.funnel_fertilized_label`),
        value: fertilized,
        detail: t(`${ns}.embryo_funnel_fertilized_detail`, {
          params: { diff: String(Math.max(0, thawed - fertilized)) },
        }),
        missing: !hasOutcomeStage("fertilized_oocytes"),
      },
      {
        id: "blastocysts",
        label: t("homepage.shared.blastocysts"),
        value: blastocysts,
        detail: t(`${ns}.embryo_funnel_blastocysts_detail`, {
          params: { diff: String(Math.max(0, fertilized - blastocysts)) },
        }),
        missing: !hasOutcomeStage("blastocysts"),
      },
      {
        id: "frozen-embryos",
        label: t(`${ns}.embryo_funnel_frozen_label`),
        value: blastocysts,
        detail: t(`${ns}.embryo_funnel_frozen_detail`),
        missing: !hasOutcomeStage("blastocysts"),
      },
      {
        id: "euploids",
        label: t(`${ns}.embryo_funnel_euploids_label`),
        value: euploids,
        detail: t(`${ns}.embryo_funnel_euploids_detail`, {
          params: { diff: String(Math.max(0, blastocysts - euploids)) },
        }),
        missing: !hasOutcomeStage("euploid_embryos"),
      },
      {
        id: "birth",
        label: t(`${ns}.funnel_birth_label`),
        value:
          liveBirthProbability == null
            ? 0
            : Math.max(0, Math.round(euploids * liveBirthProbability)),
        displayValue:
          liveBirthProbability == null
            ? undefined
            : `${Math.round(liveBirthProbability * 100)}%`,
        detail: t(`${ns}.embryo_funnel_birth_detail`),
        missing: liveBirthProbability == null,
        hideDots: true,
        progressValue: liveBirthProbability ?? undefined,
      },
    ],
    perUnitLiveBirthRate,
  };
}

function derivePerUnitLiveBirthRate(
  projection: ReturnType<typeof projectFertilityPrediction> | null,
  unitCount: number,
): RateEstimate | null {
  const liveBirthStage = projection?.stages.find(
    (stage) => stage.id === "live_births",
  );
  if (!liveBirthStage || unitCount <= 0) return null;
  return {
    mean: Math.min(1, Math.max(0, liveBirthStage.estimate.mean / unitCount)),
    low: Math.min(1, Math.max(0, liveBirthStage.estimate.low / unitCount)),
    high: Math.min(1, Math.max(0, liveBirthStage.estimate.high / unitCount)),
    unit: "probability",
    sourceIds: liveBirthStage.sourceIds,
  };
}

function InputPill({
  label,
  status,
  onPress,
}: {
  label: string;
  status: "added" | "missing";
  onPress?: () => void;
}) {
  const t = useTranslations();
  const isMissing = status === "missing";

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: isMissing ? "dashed" : "solid",
        borderColor: isMissing
          ? "rgba(92,52,32,0.22)"
          : semanticColors.border.strong,
        backgroundColor: pressed
          ? semanticColors.action.quiet.pressedBackground
          : isMissing
            ? semanticColors.surface.glass
            : semanticColors.action.quiet.background,
        paddingHorizontal: 8,
        paddingVertical: 5,
      })}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          lineHeight: 13,
          color: isMissing ? EMBER.colors.textFaint : EMBER.colors.textLight,
          fontWeight: "700",
        }}
      >
        {isMissing
          ? t("homepage.outcome_dot_funnel_rows.add_stage", {
              params: { label },
            })
          : label}
      </Text>
    </Pressable>
  );
}

type OutcomeFunnelVariant = "egg-freezing" | "embryo-freezing";

export function OutcomeEstimateFunnelSection({
  userAge,
  onAction,
  variant = "egg-freezing",
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
  variant?: OutcomeFunnelVariant;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("OutcomeEstimateFunnelSection");
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const [childrenGoal, setChildrenGoal] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dobModalVisible, setDobModalVisible] = useState(false);
  const { data: patient } = usePatient();
  const updatePatient = useUpdatePatient();
  const [dobDraft, setDobDraft] = useState(patient?.dateOfBirth ?? "");
  const amhResults = useAmhResults();
  const afcResults = useAfcResults();
  const latestAmh = amhResults[0] ?? null;
  const latestAfc = afcResults[0] ?? null;
  const [amhSheetOpen, setAmhSheetOpen] = useState(false);
  const [afcSheetOpen, setAfcSheetOpen] = useState(false);
  const [amhOverride, setAmhOverride] = useState<AmhSelection | null>(null);
  const [afcOverride, setAfcOverride] = useState<AfcSelection | null>(null);
  const [amhOptedOut, setAmhOptedOut] = useState(false);
  const [afcOptedOut, setAfcOptedOut] = useState(false);
  const effectiveAmh = amhOptedOut
    ? null
    : (amhOverride ??
      (latestAmh
        ? {
            value: latestAmh.value,
            unit: latestAmh.unit,
            sourceId: latestAmh.id,
          }
        : null));
  const effectiveAfc = afcOptedOut
    ? null
    : (afcOverride ??
      (latestAfc ? { value: latestAfc.value, sourceId: latestAfc.id } : null));
  const isEmbryoFreezing = variant === "embryo-freezing";
  const eggFunnel = useBuildEggEstimateFunnel(
    userAge,
    effectiveAmh,
    effectiveAfc,
  );
  const embryoFunnel = useBuildEmbryoEstimateFunnel(
    userAge,
    effectiveAmh,
    effectiveAfc,
  );
  const funnelData = isEmbryoFreezing ? embryoFunnel : eggFunnel;
  const funnel = funnelData.stages;
  const retrieved = funnel[0]?.value ?? 0;
  const mature = funnel[1]?.value ?? 0;
  const euploids = funnel.find((stage) => stage.id === "euploids")?.value ?? 0;
  const currentPlanningUnit = isEmbryoFreezing ? euploids : mature;
  const perUnitLiveBirthRate = funnelData.perUnitLiveBirthRate;
  const planningProjection =
    currentPlanningUnit > 0 && perUnitLiveBirthRate
      ? projectFertilityPrediction({
          kind: "cumulative_fertility_plan",
          patient: {},
          planning: {
            desiredLiveBirths: {
              value: childrenGoal,
              provenance: {
                sourceType: "patient_reported",
                sourceId: "homepage-family-goal",
              },
            },
            modality: isEmbryoFreezing ? "embryo_freezing" : "egg_freezing",
            inventoryUnit: isEmbryoFreezing
              ? "euploid_embryos"
              : "mature_oocytes",
            availableInventory: {
              value: 0,
              provenance: {
                sourceType: "model_input",
                sourceId: "homepage-known-current-inventory",
              },
            },
            expectedInventoryPerAttempt: {
              value: currentPlanningUnit,
              provenance: {
                sourceType: "model_input",
                sourceId: "homepage-current-planning-inventory",
              },
            },
            perUnitLiveBirthRate,
          },
          goal: {
            kind: "expected_stage_count",
            stage: isEmbryoFreezing ? "transfer_attempts" : "retrieval_cycles",
          },
        })
      : null;
  const planningApproved =
    (planningProjection?.presentation === "patient_safe" ||
      planningProjection?.presentation === "general_baseline") &&
    planningProjection.target.available;
  const legacyTarget = isEmbryoFreezing
    ? euploidEmbryoTargetForChildren(childrenGoal)
    : matureEggTargetForChildren(childrenGoal);
  const legacyEstimatedCycles =
    currentPlanningUnit > 0
      ? Math.max(1, Math.ceil(legacyTarget / currentPlanningUnit))
      : 0;
  const target =
    planningApproved && perUnitLiveBirthRate
      ? Math.ceil(
          childrenGoal / Math.max(perUnitLiveBirthRate.mean, Number.EPSILON),
        )
      : legacyTarget;
  const estimatedCycles = planningApproved
    ? (planningProjection?.target.estimate?.mean ?? null)
    : legacyEstimatedCycles;
  const displayTarget = target ?? legacyTarget;
  const displayEstimatedCycles = estimatedCycles ?? legacyEstimatedCycles;
  const totalDots = Math.max(retrieved, 1);
  const planningUnitLabel = isEmbryoFreezing
    ? t(`${ns}.planning_unit_embryos`)
    : t(`${ns}.planning_unit_eggs`);
  const hasAmhAndAfc = effectiveAmh !== null && effectiveAfc !== null;
  const introCopyKey = isEmbryoFreezing
    ? hasAmhAndAfc
      ? `${ns}.intro_copy_embryo_with_amh_afc`
      : effectiveAmh
        ? `${ns}.intro_copy_embryo_with_amh`
        : `${ns}.intro_copy_embryo`
    : hasAmhAndAfc
      ? `${ns}.intro_copy_egg_with_amh_afc`
      : effectiveAmh
        ? `${ns}.intro_copy_egg_with_amh`
        : `${ns}.intro_copy_egg`;
  const introCopy = t(introCopyKey);
  const planningDisclaimerKey = hasAmhAndAfc
    ? `${ns}.planning_disclaimer_with_amh_afc`
    : effectiveAmh
      ? `${ns}.planning_disclaimer_with_amh`
      : `${ns}.planning_disclaimer`;
  const ctaLabel = isEmbryoFreezing
    ? t(`${ns}.cta_check_testing`)
    : t(`${ns}.cta_test_reserve`);
  const handleOpenDocumentUpload = useCallback(
    (files: SelectedFile[]) => {
      onAction({
        label: t("homepage.test_result_document.upload_action"),
        signal: "open-document-upload",
        params: { initialCategory: "medical_records" },
        documentFiles: files,
      });
    },
    [onAction, t],
  );

  const funnelAllMissing =
    funnel.length > 0 && funnel.every((stage) => stage.missing);
  const doyleRange = doyleAgeRange();
  const ageBelowNomogram = userAge != null && userAge < doyleRange.min;
  const ageAboveNomogram = userAge != null && userAge > doyleRange.min + 14;
  // `> 44` — matches AGE_ONLY_YIELD upper bound; hardcoded add avoids a second
  // import for a constant that must match the retrieval-nomogram boundary.
  const illustrativeAge = ageBelowNomogram ? doyleRange.min : null;
  const illustrativeFunnel = useBuildEggEstimateFunnel(
    illustrativeAge ?? undefined,
    null,
    null,
  );
  const illustrativeMature = illustrativeFunnel.stages[1]?.value ?? 0;
  const illustrativeEuploids =
    illustrativeFunnel.stages.find((stage) => stage.id === "euploids")?.value ??
    0;
  const illustrativePlanningUnit = isEmbryoFreezing
    ? illustrativeEuploids
    : illustrativeMature;
  const illustrativeTarget = isEmbryoFreezing
    ? euploidEmbryoTargetForChildren(childrenGoal)
    : matureEggTargetForChildren(childrenGoal);
  const illustrativeEstimatedCycles =
    illustrativePlanningUnit > 0
      ? Math.max(1, Math.ceil(illustrativeTarget / illustrativePlanningUnit))
      : 0;
  const illustrativeDoyle =
    illustrativeAge != null && illustrativeMature > 0
      ? doyleLiveBirthEstimate(illustrativeAge, illustrativeMature)
      : null;
  const doyleProbability = illustrativeDoyle
    ? childrenGoal >= 3
      ? illustrativeDoyle.probabilityAtLeastThree
      : childrenGoal === 2
        ? illustrativeDoyle.probabilityAtLeastTwo
        : illustrativeDoyle.probabilityAtLeastOne
    : null;
  const openAmhSheet = useCallback(() => setAmhSheetOpen(true), []);

  return (
    <>
      <View style={{ gap: 12 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
                fontWeight: "400",
              }}
            >
              {introCopy}
            </Text>
          </View>
          <IconButton
            accessibilityLabel={t(`${ns}.open_detail_a11y`)}
            action={`${ns}.open_detail_a11y`}
            onPress={() => setDetailOpen(true)}
            hitSlop={8}
            size={28}
          >
            <Info size={14} color={EMBER.colors.textFaint} strokeWidth={1.8} />
          </IconButton>
        </View>

        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}>
          <InputPill
            label={
              userAge
                ? t(`${ns}.age_with_value`, {
                    params: { userAge: String(userAge) },
                  })
                : t(`${ns}.age_label`)
            }
            status={userAge ? "added" : "missing"}
            onPress={() => {
              capturePress(
                pressLabel(
                  userAge
                    ? t(`${ns}.age_with_value`, {
                        params: { userAge: String(userAge) },
                      })
                    : t(`${ns}.age_label`),
                ),
                "age_input_pill",
              );
              setDobModalVisible(true);
            }}
          />
          <InputPill
            label={
              effectiveAmh
                ? t(`${ns}.amh_with_value`, {
                    params: {
                      value: String(effectiveAmh.value),
                      unit: effectiveAmh.unit,
                    },
                  })
                : "AMH"
            }
            status={effectiveAmh ? "added" : "missing"}
            onPress={() => {
              capturePress(
                pressLabel(
                  effectiveAmh
                    ? t(`${ns}.amh_with_value`, {
                        params: {
                          value: String(effectiveAmh.value),
                          unit: effectiveAmh.unit,
                        },
                      })
                    : "AMH",
                ),
                "amh_input_pill",
              );
              setAmhSheetOpen(true);
            }}
          />
          <InputPill
            label={
              effectiveAfc
                ? t(`${ns}.afc_with_value`, {
                    params: { value: String(effectiveAfc.value) },
                  })
                : "AFC"
            }
            status={effectiveAfc ? "added" : "missing"}
            onPress={() => {
              capturePress(
                pressLabel(
                  effectiveAfc
                    ? t(`${ns}.afc_with_value`, {
                        params: { value: String(effectiveAfc.value) },
                      })
                    : "AFC",
                ),
                "afc_input_pill",
              );
              setAfcSheetOpen(true);
            }}
          />
          {isEmbryoFreezing ? (
            <InputPill
              label={t(`${ns}.semen_analysis_lower`)}
              status="missing"
              onPress={() =>
                onAction({
                  label: t(`${ns}.add_semen_analysis`),
                  screen: "AtHomeFertilityTesting",
                })
              }
            />
          ) : null}
        </View>

        {!funnelAllMissing ? (
          <ChildrenGoalCard
            childrenGoal={childrenGoal}
            setChildrenGoal={setChildrenGoal}
            summary={t(`${ns}.planning_for_summary`, {
              params: {
                childrenGoal: String(childrenGoal),
                childLabel:
                  childrenGoal === 1
                    ? t(`${ns}.child_singular`)
                    : t(`${ns}.child_plural`),
                target: String(displayTarget),
                planningUnitLabel,
                estimatedCycles: String(Math.round(displayEstimatedCycles)),
                cycleLabel:
                  Math.round(displayEstimatedCycles) === 1
                    ? t(`${ns}.cycle_singular`)
                    : t(`${ns}.cycle_plural`),
              },
            })}
          />
        ) : null}

        {funnelAllMissing ? (
          <EmptyEstimateState
            ageBelowNomogram={ageBelowNomogram}
            ageAboveNomogram={ageAboveNomogram}
            illustrativeAge={illustrativeAge}
            illustrativeFunnelStages={illustrativeFunnel.stages.filter(
              (stage) => stage.id !== "birth",
            )}
            illustrativeMature={illustrativeMature}
            illustrativeTarget={illustrativeTarget}
            illustrativeEstimatedCycles={illustrativeEstimatedCycles}
            planningUnitLabel={planningUnitLabel}
            doyleProbability={doyleProbability}
            childrenGoal={childrenGoal}
            setChildrenGoal={setChildrenGoal}
            onAddAmh={openAmhSheet}
          />
        ) : (
          <>
            <OutcomeDotFunnelRows
              stages={funnel}
              totalDots={totalDots}
              embedded
            />

            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                lineHeight: 16,
                color: EMBER.colors.textFaint,
                fontWeight: "400",
              }}
            >
              {t(planningDisclaimerKey)}
            </Text>

            <CompactActionButton
              label={ctaLabel}
              action={
                isEmbryoFreezing
                  ? `${ns}.cta_check_testing`
                  : `${ns}.cta_test_reserve`
              }
              variant="secondary"
              size="sm"
              trailingIcon={ArrowRight}
              onPress={() =>
                onAction({
                  label: ctaLabel,
                  screen: "AtHomeFertilityTesting",
                })
              }
              style={{ alignSelf: "flex-start" }}
            />
          </>
        )}
      </View>

      <AmhSourceSheet
        open={amhSheetOpen}
        onClose={() => setAmhSheetOpen(false)}
        onSelect={(selection) => {
          setAmhOptedOut(false);
          setAmhOverride(selection);
        }}
        onClear={() => {
          setAmhOverride(null);
          setAmhOptedOut(true);
        }}
        onOpenDocuments={handleOpenDocumentUpload}
        currentSourceId={effectiveAmh?.sourceId ?? null}
        hasActiveAmh={effectiveAmh !== null}
      />

      <AfcSourceSheet
        open={afcSheetOpen}
        onClose={() => setAfcSheetOpen(false)}
        onSelect={(selection) => {
          setAfcOptedOut(false);
          setAfcOverride(selection);
        }}
        onClear={() => {
          setAfcOverride(null);
          setAfcOptedOut(true);
        }}
        onOpenDocuments={handleOpenDocumentUpload}
        currentSourceId={effectiveAfc?.sourceId ?? null}
        hasActiveAfc={effectiveAfc !== null}
      />

      <Dialog
        visible={dobModalVisible}
        onClose={() => setDobModalVisible(false)}
        closeButton="minimal"
      >
        <DialogIcon
          source={DATE_OF_BIRTH_DIALOG_ILLUSTRATION}
          width={82}
          height={98}
          style={{ marginBottom: 14 }}
          testID="date-of-birth-dialog-illustration"
        />
        <DialogHeader
          align="center"
          title={t(`${ns}.date_of_birth`)}
          subtitle={t(`${ns}.date_of_birth_subtitle`)}
          style={{ alignItems: "center", width: "100%", marginBottom: 16 }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
        <View>
          <DateField
            label={t(`${ns}.date_of_birth_field`)}
            labelIcon={CalendarDays}
            value={dobDraft}
            onChange={setDobDraft}
          />
        </View>
        <DialogActions style={{ marginTop: 24 }}>
          <PrimaryButton
            label={t(`${ns}.save`)}
            onPress={() => {
              updatePatient.mutate({ dateOfBirth: dobDraft || null });
              setDobModalVisible(false);
            }}
            disabled={!dobDraft}
          />
        </DialogActions>
      </Dialog>

      <DetailModal
        slide={
          detailOpen
            ? {
                detail: {
                  title: t(`${ns}.where_numbers_come_from`),
                  sections: [
                    {
                      title: t("homepage.shared.cycle_estimate"),
                      body: isEmbryoFreezing
                        ? t(`${ns}.detail_cycle_estimate_embryo`)
                        : t(`${ns}.detail_cycle_estimate_egg`),
                    },
                    {
                      title: t(`${ns}.attrition_funnel`),
                      body: isEmbryoFreezing
                        ? t(`${ns}.detail_attrition_embryo`)
                        : t(`${ns}.detail_attrition_egg`),
                    },
                    {
                      title: t(`${ns}.family_planning_target`),
                      body: isEmbryoFreezing
                        ? t(`${ns}.detail_family_target_embryo`)
                        : t(`${ns}.detail_family_target_egg`),
                    },
                  ],
                },
              }
            : null
        }
        onClose={() => setDetailOpen(false)}
      />
    </>
  );
}

type ChildrenGoalCardProps = {
  childrenGoal: number;
  setChildrenGoal: (updater: (current: number) => number) => void;
  summary: string;
  probabilityLine?: string | null;
};

function ChildrenGoalCard({
  childrenGoal,
  setChildrenGoal,
  summary,
  probabilityLine,
}: ChildrenGoalCardProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("ChildrenGoalCard");
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  return (
    <CardSurface variant="panel" style={{ padding: 18, gap: 14 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 18,
            color: EMBER.colors.text,
            fontWeight: "700",
            flex: 1,
          }}
        >
          {t(`${ns}.how_many_children`)}
        </Text>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t(`${ns}.reduce_children_goal`)),
                `${ns}.reduce_children_goal`,
              );
              setChildrenGoal((current) => Math.max(1, current - 1));
            }}
            accessibilityRole="button"
            accessibilityLabel={t(`${ns}.reduce_children_goal`)}
            style={({ pressed }) => ({
              width: 36,
              height: 36,
              borderRadius: 999,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: pressed
                ? "rgba(255,255,255,0.9)"
                : "rgba(255,255,255,0.72)",
              borderWidth: 1,
              borderColor: semanticColors.border.strong,
            })}
          >
            <Text
              style={{
                color: EMBER.colors.text,
                fontWeight: "800",
                fontSize: 16,
                lineHeight: 20,
              }}
            >
              −
            </Text>
          </Pressable>
          <Text
            className="font-dm-sans"
            style={{
              minWidth: 26,
              fontSize: 22,
              lineHeight: 26,
              color: EMBER.colors.text,
              fontWeight: "800",
              textAlign: "center",
            }}
          >
            {childrenGoal}
          </Text>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t(`${ns}.increase_children_goal`)),
                `${ns}.increase_children_goal`,
              );
              setChildrenGoal((current) => current + 1);
            }}
            accessibilityRole="button"
            accessibilityLabel={t(`${ns}.increase_children_goal`)}
            style={({ pressed }) => ({
              width: 36,
              height: 36,
              borderRadius: 999,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: pressed
                ? "rgba(255,255,255,0.9)"
                : "rgba(255,255,255,0.72)",
              borderWidth: 1,
              borderColor: semanticColors.border.strong,
            })}
          >
            <Text
              style={{
                color: EMBER.colors.text,
                fontWeight: "800",
                fontSize: 16,
                lineHeight: 20,
              }}
            >
              +
            </Text>
          </Pressable>
        </View>
      </View>

      <View style={{ gap: 6 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 18,
            color: EMBER.colors.textMid,
            fontWeight: "400",
          }}
        >
          {summary}
        </Text>
        {probabilityLine ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              lineHeight: 18,
              color: EMBER.colors.text,
              fontWeight: "600",
            }}
          >
            {probabilityLine}
          </Text>
        ) : null}
      </View>
    </CardSurface>
  );
}

type EmptyEstimateStateProps = {
  ageBelowNomogram: boolean;
  ageAboveNomogram: boolean;
  illustrativeAge: number | null;
  illustrativeFunnelStages: OutcomeDotFunnelStage[];
  illustrativeMature: number;
  illustrativeTarget: number;
  illustrativeEstimatedCycles: number;
  planningUnitLabel: string;
  doyleProbability: number | null;
  childrenGoal: number;
  setChildrenGoal: (updater: (current: number) => number) => void;
  onAddAmh: () => void;
};

function EmptyEstimateState({
  ageBelowNomogram,
  ageAboveNomogram,
  illustrativeAge,
  illustrativeFunnelStages,
  illustrativeMature,
  illustrativeTarget,
  illustrativeEstimatedCycles,
  planningUnitLabel,
  doyleProbability,
  childrenGoal,
  setChildrenGoal,
  onAddAmh,
}: EmptyEstimateStateProps) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const hintKey = ageBelowNomogram
    ? `${ns}.young_age_baseline_hint`
    : ageAboveNomogram
      ? `${ns}.advanced_age_needs_amh`
      : `${ns}.no_estimate_add_amh`;
  const showIllustrativeFunnel =
    ageBelowNomogram && illustrativeAge != null && illustrativeMature > 0;
  return (
    <View style={{ gap: 14 }}>
      <View>
        {/* The eggs illustration stands on its own, with no circle behind. */}
        <EmptyState
          illustration={<OrganicMiniIcon kind="eggs" size={36} />}
          title={t(hintKey)}
          subtitle={
            showIllustrativeFunnel
              ? t(`${ns}.illustrative_baseline_note`, {
                  params: { baselineAge: String(illustrativeAge) },
                })
              : undefined
          }
          density="compact"
          style={{ paddingVertical: 0, paddingHorizontal: 0 }}
        />
      </View>

      {showIllustrativeFunnel ? (
        <OutcomeDotFunnelRows
          stages={illustrativeFunnelStages}
          totalDots={Math.max(illustrativeFunnelStages[0]?.value ?? 0, 1)}
          embedded
        />
      ) : null}

      <ChildrenGoalCard
        childrenGoal={childrenGoal}
        setChildrenGoal={setChildrenGoal}
        summary={
          illustrativeEstimatedCycles > 0
            ? t(`${ns}.planning_for_summary`, {
                params: {
                  childrenGoal: String(childrenGoal),
                  childLabel:
                    childrenGoal === 1
                      ? t(`${ns}.child_singular`)
                      : t(`${ns}.child_plural`),
                  target: String(illustrativeTarget),
                  planningUnitLabel,
                  estimatedCycles: String(illustrativeEstimatedCycles),
                  cycleLabel:
                    illustrativeEstimatedCycles === 1
                      ? t(`${ns}.cycle_singular`)
                      : t(`${ns}.cycle_plural`),
                },
              })
            : t(`${ns}.planning_for_summary_empty`, {
                params: {
                  childrenGoal: String(childrenGoal),
                  childLabel:
                    childrenGoal === 1
                      ? t(`${ns}.child_singular`)
                      : t(`${ns}.child_plural`),
                },
              })
        }
        probabilityLine={
          doyleProbability != null
            ? t(`${ns}.illustrative_live_birth_probability`, {
                params: {
                  percent: String(Math.round(doyleProbability * 100)),
                  childrenGoal: String(childrenGoal),
                  childLabel:
                    childrenGoal === 1
                      ? t(`${ns}.child_singular`)
                      : t(`${ns}.child_plural`),
                },
              })
            : null
        }
      />

      <CompactActionButton
        label={t(`${ns}.add_amh_cta`)}
        action={`${ns}.add_amh_cta`}
        variant="secondary"
        size="sm"
        trailingIcon={ArrowRight}
        onPress={onAddAmh}
        style={{ alignSelf: "flex-start" }}
      />
    </View>
  );
}

function EstimateBridgeRow({
  userAge,
  onAction,
  showNestedHeading = true,
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
  showNestedHeading?: boolean;
}) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const estimate = useEstimateForAge(userAge);

  return (
    <CardSurface
      style={{
        marginTop: 12,
        padding: 12,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          width: 76,
          height: 66,
          alignItems: "center",
          justifyContent: "center",
          marginLeft: -4,
        }}
      >
        <OrganicMiniIcon kind="eggs" size={56} />
      </View>

      <View style={{ flex: 1 }}>
        {showNestedHeading ? (
          <>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                lineHeight: 13,
                color: EMBER.colors.textLight,
                fontWeight: "700",
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              {t(`${ns}.get_personalized_estimate`)}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                lineHeight: 19,
                color: EMBER.colors.text,
                fontWeight: "700",
                marginTop: 3,
              }}
            >
              {t(`${ns}.how_many_eggs_question`)}
            </Text>
          </>
        ) : null}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: showNestedHeading ? 12 : 13,
            lineHeight: showNestedHeading ? 17 : 19,
            color: EMBER.colors.textMid,
            fontWeight: "400",
            marginTop: showNestedHeading ? 2 : 0,
          }}
        >
          {t(`${ns}.bridge_estimate_text`, {
            params: { value: estimate.value, body: estimate.body },
          })}
        </Text>
      </View>

      <IconButton
        accessibilityLabel={t(`${ns}.know_your_numbers`)}
        action={`${ns}.know_your_numbers`}
        onPress={() =>
          onAction({
            label: t(`${ns}.know_your_numbers`),
            screen: "MedicalHistory",
          })
        }
        size={34}
        surface="frosted"
      >
        <ArrowRight size={15} color={EMBER.colors.text} strokeWidth={1.8} />
      </IconButton>
    </CardSurface>
  );
}

export function CoverageActionRow({
  onAction,
  variant = "egg-freezing",
}: {
  onAction: (action: PlanAction) => void;
  variant?: CoverageEstimateVariant;
}) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const title =
    variant === "sperm-freezing"
      ? t(`${ns}.coverage_title_sperm`)
      : t(`${ns}.coverage_title_default`);
  const subtitle =
    variant === "sperm-freezing"
      ? t(`${ns}.coverage_subtitle_sperm`)
      : t(`${ns}.coverage_subtitle_default`);

  return (
    <TimelineSubtaskCard
      id={`precycle-${variant}.coverage-estimate`}
      title={title}
      subtitle={subtitle}
      titleIcon={<OrganicMiniIcon kind="costs" size={34} />}
      collapsedPreviewHeight={68}
    >
      <CoverageEstimatePreviewSection onAction={onAction} variant={variant} />
    </TimelineSubtaskCard>
  );
}

type CoverageEstimateVariant =
  "egg-freezing" | "embryo-freezing" | "sperm-freezing";

type CoverageCriterion = {
  id: string;
  label: string;
  done: boolean;
  icon: LucideIcon;
  doneLabel: string;
  missingLabel: string;
};

type CoveragePreviewCard = {
  id: string;
  tag: string;
  title: string;
  body: string;
  footer: string;
};

function useCoveragePreviewCards(): CoveragePreviewCard[] {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  return [
    {
      id: "treatment",
      tag: t(`${ns}.coverage_tag_treatment`),
      title: t("homepage.shared.cycle_estimate"),
      body: t(`${ns}.coverage_treatment_body`),
      footer: t(`${ns}.coverage_treatment_footer`),
    },
    {
      id: "meds",
      tag: t(`${ns}.coverage_tag_medication`),
      title: t(`${ns}.coverage_medication_title`),
      body: t(`${ns}.coverage_medication_body`),
      footer: t(`${ns}.coverage_medication_footer`),
    },
    {
      id: "benefits",
      tag: t(`${ns}.coverage_tag_benefits`),
      title: t(`${ns}.coverage_employer_title`),
      body: t(`${ns}.coverage_employer_body`),
      footer: t(`${ns}.coverage_employer_footer`),
    },
  ];
}

function useSpermCoveragePreviewCards(): CoveragePreviewCard[] {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  return [
    {
      id: "banking",
      tag: t(`${ns}.coverage_tag_banking`),
      title: t(`${ns}.coverage_sample_title`),
      body: t(`${ns}.coverage_sample_body`),
      footer: t(`${ns}.coverage_sample_footer`),
    },
    {
      id: "repeat-testing",
      tag: t(`${ns}.coverage_tag_retest`),
      title: t(`${ns}.coverage_retest_title`),
      body: t(`${ns}.coverage_retest_body`),
      footer: t(`${ns}.coverage_retest_footer`),
    },
    {
      id: "benefits",
      tag: t(`${ns}.coverage_tag_benefits`),
      title: t(`${ns}.coverage_employer_title`),
      body: t(`${ns}.coverage_sperm_employer_body`),
      footer: t(`${ns}.coverage_employer_footer`),
    },
  ];
}

function CoverageCriterionPill({
  criterion,
}: {
  criterion: CoverageCriterion;
}) {
  // Added criteria read as filled; the ones still missing as outlines.
  return (
    <Tag
      label={criterion.done ? criterion.doneLabel : criterion.missingLabel}
      variant={criterion.done ? "soft" : "outline"}
      icon={criterion.done ? CheckCircle2 : criterion.icon}
    />
  );
}

function CoveragePreviewEstimateCard({
  item,
  width,
  locked,
}: {
  item: CoveragePreviewCard;
  width: number;
  locked: boolean;
}) {
  return (
    <CardSurface
      variant="panel"
      style={{
        width,
        minHeight: 212,
        padding: 14,
        opacity: locked ? 0.46 : 1,
        ...(Platform.OS === "web" && locked
          ? ({ filter: "blur(2px)" } as Record<string, string>)
          : null),
      }}
    >
      <Tag label={item.tag} style={{ alignSelf: "flex-start" }} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 16,
          lineHeight: 21,
          color: EMBER.colors.text,
          fontWeight: "700",
          marginTop: 10,
        }}
      >
        {item.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 10,
        }}
      >
        {item.body}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textLight,
          fontWeight: "700",
          marginTop: "auto",
          paddingTop: 12,
        }}
      >
        {item.footer}
      </Text>
    </CardSurface>
  );
}

function CoverageEstimatePreviewSection({
  onAction,
  variant,
}: {
  onAction: (action: PlanAction) => void;
  variant: CoverageEstimateVariant;
}) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const { data: patient } = usePatient();
  const { hasInsurance } = useCoverage();
  const hasEmployerBenefitsKnown =
    patient?.fertilityIntent?.hasEmployerBenefits != null;
  const hasEmployerBenefits =
    patient?.fertilityIntent?.hasEmployerBenefits === true;
  const hasCoverageStatus = hasInsurance || hasEmployerBenefitsKnown;
  const eggCoverageCards = useCoveragePreviewCards();
  const spermCoverageCards = useSpermCoveragePreviewCards();
  const criteria: CoverageCriterion[] = [
    {
      id: "insurance",
      label: t(`${ns}.criterion_health_insurance`),
      done: hasInsurance,
      doneLabel: t(`${ns}.criterion_insurance_added`),
      missingLabel: t(`${ns}.criterion_add_insurance`),
      icon: ShieldCheck,
    },
    {
      id: "employer",
      label: t(`${ns}.criterion_employer_benefits`),
      done: hasEmployerBenefitsKnown,
      doneLabel: hasEmployerBenefits
        ? t(`${ns}.criterion_benefits_added`)
        : t(`${ns}.criterion_benefits_checked`),
      missingLabel: t(`${ns}.criterion_check_benefits`),
      icon: BriefcaseBusiness,
    },
    {
      id: "status",
      label: t(`${ns}.criterion_coverage_status`),
      done: hasCoverageStatus,
      doneLabel: t(`${ns}.criterion_status_checked`),
      missingLabel: t(`${ns}.criterion_confirm_status`),
      icon: BadgeDollarSign,
    },
  ];
  const locked = !hasCoverageStatus;
  const action: PlanAction = locked
    ? {
        label: t(`${ns}.action_check_coverage_status`),
        signal: "open-coverage-status-modal",
      }
    : {
        label: t(`${ns}.action_open_cost_estimate`),
        screen: "CostsOverviewScreen",
      };
  const previewCards =
    variant === "sperm-freezing" ? spermCoverageCards : eggCoverageCards;

  return (
    <View style={{ borderRadius: 16, overflow: "hidden" }}>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 7,
          marginBottom: 12,
        }}
      >
        {criteria.map((criterion) => (
          <CoverageCriterionPill key={criterion.id} criterion={criterion} />
        ))}
      </View>

      <View>
        <HorizontalSnapCarousel
          items={previewCards}
          gap={10}
          fallbackCardWidth={244}
          getCardWidth={(containerWidth) =>
            Math.min(268, Math.max(232, containerWidth * 0.82))
          }
          keyExtractor={(item) => item.id}
          renderItem={(item, width) => (
            <CoveragePreviewEstimateCard
              item={item}
              width={width}
              locked={locked}
            />
          )}
          contentContainerStyle={{ paddingRight: 2 }}
        />

        <View
          pointerEvents="box-none"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            alignItems: "center",
            justifyContent: "center",
            padding: 18,
          }}
        >
          <View
            style={{
              width: "100%",
              borderRadius: 16,
              borderWidth: 1,
              borderColor: semanticColors.border.glass,
              backgroundColor: locked
                ? "rgba(255,255,255,0.72)"
                : semanticColors.surface.input,
              padding: 14,
              ...(Platform.OS === "web"
                ? ({
                    backdropFilter: locked ? "blur(8px)" : "blur(5px)",
                    WebkitBackdropFilter: locked ? "blur(8px)" : "blur(5px)",
                  } as Record<string, string>)
                : null),
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
            >
              {locked ? (
                <Lock size={15} color={EMBER.colors.text} strokeWidth={1.8} />
              ) : (
                <CheckCircle2
                  size={15}
                  color={EMBER.colors.text}
                  strokeWidth={1.8}
                />
              )}
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 14,
                  lineHeight: 18,
                  color: EMBER.colors.text,
                  fontWeight: "700",
                }}
              >
                {locked
                  ? t(`${ns}.unlock_cost_estimate`)
                  : t(`${ns}.coverage_status_ready`)}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 18,
                color: EMBER.colors.textMid,
                fontWeight: "400",
                marginTop: 7,
              }}
            >
              {locked
                ? t(`${ns}.unlock_explainer`)
                : t(`${ns}.ready_explainer`)}
            </Text>
            <CompactActionButton
              label={action.label}
              variant="primary"
              size="sm"
              trailingIcon={ArrowRight}
              onPress={() => onAction(action)}
              style={{ alignSelf: "flex-start", marginTop: 11 }}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

type OptimizationOddsSlide = {
  id: string;
  label: string;
  title: string;
  body: string;
  bullets: string[];
  cta?: PlanAction;
  detail: {
    title: string;
    sections: { title: string; body: string }[];
  };
};

type DetailModalSlide = {
  detail: {
    title: string;
    sections: { title: string; body: string }[];
  };
};

function useOptimizationOddsSlides(): OptimizationOddsSlide[] {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  return [
    {
      id: "step-1-baseline",
      label: t(`${ns}.step_1`),
      title: t(`${ns}.opt_step_1_title`),
      body: t(`${ns}.opt_step_1_body`),
      bullets: [
        t(`${ns}.opt_step_1_bullet_1`),
        t(`${ns}.opt_step_1_bullet_2`),
        t(`${ns}.opt_step_1_bullet_3`),
      ],
      cta: {
        label: t(`${ns}.cta_check_testing`),
        screen: "AtHomeFertilityTesting",
      },
      detail: {
        title: t(`${ns}.opt_step_1_detail_title`),
        sections: [
          {
            title: t(`${ns}.opt_step_1_s1_title`),
            body: t(`${ns}.opt_step_1_s1_body`),
          },
          {
            title: t(`${ns}.opt_step_1_s2_title`),
            body: t(`${ns}.opt_step_1_s2_body`),
          },
        ],
      },
    },
    {
      id: "step-2-exposures",
      label: t(`${ns}.step_2`),
      title: t("homepage.shared.reduce_avoidable_exposures"),
      body: t(`${ns}.opt_step_2_body`),
      bullets: [
        t(`${ns}.opt_step_2_bullet_1`),
        t(`${ns}.opt_step_2_bullet_2`),
        t(`${ns}.opt_step_2_bullet_3`),
      ],
      cta: {
        label: t(`${ns}.opt_step_2_cta`),
        screen: "ProgramsScreen",
        params: { focus: "optimization" },
      },
      detail: {
        title: t(`${ns}.opt_step_2_detail_title`),
        sections: [
          {
            title: t(`${ns}.opt_step_2_s1_title`),
            body: t(`${ns}.opt_step_2_s1_body`),
          },
          {
            title: t(`${ns}.opt_step_2_s2_title`),
            body: t(`${ns}.opt_step_2_s2_body`),
          },
        ],
      },
    },
    {
      id: "step-3-metabolic",
      label: t(`${ns}.step_3`),
      title: t(`${ns}.opt_step_3_title`),
      body: t(`${ns}.opt_step_3_body`),
      bullets: [
        t(`${ns}.opt_step_3_bullet_1`),
        t(`${ns}.opt_step_3_bullet_2`),
        t(`${ns}.opt_step_3_bullet_3`),
      ],
      cta: {
        label: t(`${ns}.opt_step_3_cta`),
        screen: "MedicalHistory",
      },
      detail: {
        title: t(`${ns}.opt_step_3_detail_title`),
        sections: [
          {
            title: t(`${ns}.opt_step_3_s1_title`),
            body: t(`${ns}.opt_step_3_s1_body`),
          },
          {
            title: t(`${ns}.opt_step_3_s2_title`),
            body: t(`${ns}.opt_step_3_s2_body`),
          },
        ],
      },
    },
    {
      id: "step-4-supplements",
      label: t(`${ns}.step_4`),
      title: t(`${ns}.opt_step_4_title`),
      body: t(`${ns}.opt_step_4_body`),
      bullets: [
        t(`${ns}.opt_step_4_bullet_1`),
        t(`${ns}.opt_step_4_bullet_2`),
        t(`${ns}.opt_step_4_bullet_3`),
      ],
      cta: {
        label: t(`${ns}.opt_step_4_cta`),
        screen: "SupplementsScreen",
      },
      detail: {
        title: t(`${ns}.opt_step_4_detail_title`),
        sections: [
          {
            title: t(`${ns}.opt_step_4_s1_title`),
            body: t(`${ns}.opt_step_4_s1_body`),
          },
          {
            title: t(`${ns}.opt_step_4_s2_title`),
            body: t(`${ns}.opt_step_4_s2_body`),
          },
          {
            title: t(`${ns}.opt_step_4_s3_title`),
            body: t(`${ns}.opt_step_4_s3_body`),
          },
        ],
      },
    },
    {
      id: "step-5-plan",
      label: t(`${ns}.step_5`),
      title: t(`${ns}.opt_step_5_title`),
      body: t(`${ns}.opt_step_5_body`),
      bullets: [
        t(`${ns}.opt_step_5_bullet_1`),
        t(`${ns}.opt_step_5_bullet_2`),
        t(`${ns}.opt_step_5_bullet_3`),
      ],
      cta: {
        label: t(`${ns}.opt_step_5_cta`),
        screen: "ProgramsScreen",
        params: { focus: "optimization" },
      },
      detail: {
        title: t(`${ns}.opt_step_5_detail_title`),
        sections: [
          {
            title: t(`${ns}.opt_step_5_s1_title`),
            body: t(`${ns}.opt_step_5_s1_body`),
          },
          {
            title: t(`${ns}.opt_step_5_s2_title`),
            body: t(`${ns}.opt_step_5_s2_body`),
          },
        ],
      },
    },
  ];
}

function OptimizationOddsCard({
  slide,
  width,
  equalHeight,
  onOpen,
  onAction,
  onMeasure,
}: {
  slide: OptimizationOddsSlide;
  width: number;
  equalHeight?: number;
  onOpen: () => void;
  onAction: (action: PlanAction) => void;
  onMeasure: (id: string, height: number) => void;
}) {
  const t = useTranslations();
  return (
    <View
      onLayout={(event) => {
        onMeasure(slide.id, Math.ceil(event.nativeEvent.layout.height));
      }}
      style={{
        ...cardSurface({ variant: "panel" }),
        width,
        height: equalHeight,
        paddingHorizontal: 15,
        paddingTop: 10,
        paddingBottom: 15,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10,
            lineHeight: 13,
            color: EMBER.colors.textLight,
            fontWeight: "700",
            letterSpacing: 0.35,
            textTransform: "uppercase",
          }}
        >
          {slide.label}
        </Text>
        <IconButton
          accessibilityLabel={t(
            "homepage.egg_freezing_biology_carousel.open_detail_for",
            { params: { title: slide.title } },
          )}
          action={slide.id}
          onPress={() => onOpen()}
          hitSlop={8}
          size={28}
        >
          <Info size={14} color={EMBER.colors.textFaint} strokeWidth={1.8} />
        </IconButton>
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 17,
          lineHeight: 21,
          color: EMBER.colors.text,
          fontWeight: "700",
          marginTop: 12,
        }}
      >
        {slide.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 6,
        }}
      >
        {slide.body}
      </Text>

      <View style={{ gap: 8, marginTop: 12 }}>
        {slide.bullets.map((bullet) => (
          <View
            key={bullet}
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 7 }}
          >
            <CheckCircle2
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
              style={{ marginTop: 2 }}
            />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textMid,
                fontWeight: "400",
              }}
            >
              {bullet}
            </Text>
          </View>
        ))}
      </View>

      <View style={{ flex: 1 }} />

      {slide.cta ? (
        <CompactActionButton
          label={slide.cta.label}
          action={`${slide.id}_cta`}
          variant="secondary"
          size="sm"
          trailingIcon={ArrowRight}
          onPress={() => onAction(slide.cta!)}
          style={{ alignSelf: "flex-start", marginTop: 12 }}
        />
      ) : null}
    </View>
  );
}

function OptimizationOddsCarousel({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const slides = useOptimizationOddsSlides();
  const [selectedSlide, setSelectedSlide] =
    useState<OptimizationOddsSlide | null>(null);
  const [cardHeights, setCardHeights] = useState<Record<string, number>>({});
  const optimizationMeasureKey = slides
    .map(
      (slide) =>
        `${slide.id}:${slide.title}:${slide.body}:${slide.bullets.join(",")}:${
          slide.cta?.label ?? ""
        }`,
    )
    .join("|");
  const measuredHeights = slides
    .map((slide) => cardHeights[slide.id])
    .filter((height): height is number => typeof height === "number");
  const equalCardHeight =
    measuredHeights.length >= slides.length
      ? Math.max(...measuredHeights)
      : undefined;
  const handleCardMeasure = useCallback((id: string, height: number) => {
    setCardHeights((current) => {
      if (current[id] === height) {
        return current;
      }

      return { ...current, [id]: height };
    });
  }, []);

  useEffect(() => {
    setCardHeights({});
  }, [optimizationMeasureKey]);

  return (
    <>
      <HorizontalSnapCarousel
        key={optimizationMeasureKey}
        items={slides}
        gap={10}
        fallbackCardWidth={252}
        getCardWidth={(containerWidth) =>
          Math.min(270, Math.max(232, containerWidth * 0.78))
        }
        keyExtractor={(slide) => slide.id}
        renderItem={(slide, cardWidth) => (
          <OptimizationOddsCard
            slide={slide}
            width={cardWidth}
            equalHeight={equalCardHeight}
            onOpen={() => setSelectedSlide(slide)}
            onAction={onAction}
            onMeasure={handleCardMeasure}
          />
        )}
        contentContainerStyle={{ paddingRight: 4 }}
      />
      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </>
  );
}

function DeepBiologyCarouselContent({
  userAge,
  onAction,
  onOpenSlide,
  slides: slidesProp,
  showEstimateBridge = true,
  illustrationVariant = "flat",
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
  onOpenSlide: (slide: DeepBiologySlide) => void;
  slides?: DeepBiologySlide[];
  showEstimateBridge?: boolean;
  illustrationVariant?: IllustrationVariant;
}) {
  const defaultSlides = useDeepBiologySlides();
  const slides = slidesProp ?? defaultSlides;
  const [cardHeights, setCardHeights] = useState<Record<string, number>>({});
  const measuredCardCount = Object.keys(cardHeights).length;
  const equalCardHeight =
    measuredCardCount >= slides.length
      ? Math.max(...Object.values(cardHeights))
      : undefined;
  const handleCardMeasure = useCallback((id: string, height: number) => {
    setCardHeights((current) => {
      if (current[id] === height) {
        return current;
      }

      return { ...current, [id]: height };
    });
  }, []);

  return (
    <>
      <HorizontalSnapCarousel
        items={slides}
        gap={10}
        fallbackCardWidth={252}
        getCardWidth={(containerWidth) =>
          Math.min(270, Math.max(232, containerWidth * 0.78))
        }
        keyExtractor={(slide) => slide.id}
        renderItem={(slide, cardWidth) => (
          <DeepBiologyCard
            slide={slide}
            width={cardWidth}
            equalHeight={equalCardHeight}
            onOpen={() => onOpenSlide(slide)}
            onAction={onAction}
            onMeasure={handleCardMeasure}
            illustrationVariant={illustrationVariant}
          />
        )}
        contentContainerStyle={{ paddingRight: 4 }}
      />
      {showEstimateBridge ? (
        <EstimateBridgeRow userAge={userAge} onAction={onAction} />
      ) : null}
    </>
  );
}

export function BiologyDeepCarousel({
  userAge,
  onAction,
  slides,
  showEstimateBridge = false,
  illustrationVariant = "organic-explanatory",
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
  slides?: DeepBiologySlide[];
  showEstimateBridge?: boolean;
  illustrationVariant?: IllustrationVariant;
}) {
  const [selectedSlide, setSelectedSlide] = useState<DeepBiologySlide | null>(
    null,
  );

  return (
    <>
      <DeepBiologyCarouselContent
        userAge={userAge}
        onAction={onAction}
        onOpenSlide={setSelectedSlide}
        slides={slides}
        showEstimateBridge={showEstimateBridge}
        illustrationVariant={illustrationVariant}
      />
      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </>
  );
}

/**
 * A carousel slide's "more detail", in the canonical dialog: the shared
 * chrome and close button, the dialog header for the title, and subsection
 * labels over dialog body text for each section.
 */
function DetailModal({
  slide,
  onClose,
}: {
  slide: DetailModalSlide | null;
  onClose: () => void;
}) {
  const { height } = useWindowDimensions();
  // A pixel bound on every layer, as the other scrolling dialogs do: a
  // percentage resolves against nothing on web and slices the card mid-text.
  const dialogMaxHeight = Math.min(640, height * 0.85);
  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      visible={!!slide}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
      testID="biology-detail-dialog"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{ maxHeight: dialogMaxHeight }}
    >
      {slide ? (
        <View style={{ flex: 1, minHeight: 0 }}>
          <DialogHeader
            title={slide.detail.title}
            style={{ marginBottom: 16 }}
          />
          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{ flexShrink: 1 }}
            contentContainerStyle={{ gap: 16 }}
          >
            {slide.detail.sections.map((section) => (
              <View key={section.title}>
                <SectionTitle level="subsection">{section.title}</SectionTitle>
                <DialogBodyText>{section.body}</DialogBodyText>
              </View>
            ))}
          </ScrollView>
        </View>
      ) : null}
    </Dialog>
  );
}

function DeepBiologyCard({
  slide,
  width,
  equalHeight,
  onOpen,
  onAction,
  onMeasure,
  illustrationVariant,
}: {
  slide: DeepBiologySlide;
  width: number;
  equalHeight?: number;
  onOpen: () => void;
  onAction: (action: PlanAction) => void;
  onMeasure: (id: string, height: number) => void;
  illustrationVariant: IllustrationVariant;
}) {
  const t = useTranslations();
  return (
    <View
      onLayout={(event) => {
        onMeasure(slide.id, Math.ceil(event.nativeEvent.layout.height));
      }}
      style={{
        ...cardSurface({ variant: "panel" }),
        width,
        minHeight: equalHeight,
        paddingHorizontal: 15,
        paddingTop: 10,
        paddingBottom: 15,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10,
            lineHeight: 13,
            color: EMBER.colors.textLight,
            fontWeight: "700",
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {slide.label}
        </Text>
        <IconButton
          accessibilityLabel={t(
            "homepage.egg_freezing_biology_carousel.open_detail_for",
            { params: { title: slide.title } },
          )}
          action={slide.id}
          onPress={() => onOpen()}
          hitSlop={8}
          size={28}
        >
          <Info size={14} color={EMBER.colors.textFaint} strokeWidth={1.8} />
        </IconButton>
      </View>

      {illustrationVariant === "organic-explanatory" ? (
        <OrganicExplanatoryIllustration kind={slide.visual} />
      ) : illustrationVariant === "organic" ? (
        <OrganicBiologyIllustration kind={slide.visual} />
      ) : (
        <BiologyIllustration kind={slide.visual} />
      )}

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 17,
          lineHeight: 21,
          color: EMBER.colors.text,
          fontWeight: "700",
        }}
      >
        {slide.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 19,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 8,
        }}
      >
        {slide.body}
      </Text>

      <View style={{ flex: 1 }} />

      <View
        style={{
          marginTop: 12,
          gap: 12,
        }}
      >
        <View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 15,
              color: EMBER.colors.text,
              fontWeight: "700",
            }}
          >
            {slide.calloutLabel}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textMid,
              fontWeight: "400",
              marginTop: 3,
            }}
          >
            {slide.callout}
          </Text>
        </View>

        {slide.cta ? (
          <CompactActionButton
            label={slide.cta.label}
            action={`${slide.id}_cta`}
            variant="secondary"
            size="sm"
            trailingIcon={ArrowRight}
            onPress={() => onAction(slide.cta!)}
            style={{ alignSelf: "flex-start" }}
          />
        ) : null}
      </View>
    </View>
  );
}

export function EggFreezingBiologyDeepCarouselCard({
  userAge,
  onAction,
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("EggFreezingBiologyDeepCarouselCard");
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const [selectedSlide, setSelectedSlide] = useState<DeepBiologySlide | null>(
    null,
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMarkedRead, setIsMarkedRead] = useState(false);
  const handleStatusPress = useCallback(() => {
    capturePress(
      pressLabel(isMarkedRead ? t(`${ns}.read`) : t(`${ns}.mark_read`)),
      isMarkedRead ? `${ns}.read` : `${ns}.mark_read`,
    );
    if (!isMarkedRead) {
      setIsMarkedRead(true);
      setIsCollapsed(true);
      return;
    }

    setIsCollapsed((current) => !current);
  }, [capturePress, isMarkedRead, ns, t]);

  return (
    <View style={{ marginTop: 16 }}>
      <View style={{ paddingHorizontal: 2, marginBottom: 8 }}>
        <NeutralStepPill label={t(`${ns}.step_1_understand`)} />
      </View>
      <View
        style={{
          marginBottom: isCollapsed ? 0 : 12,
          paddingHorizontal: 2,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                isCollapsed
                  ? t(`${ns}.expand_how_it_works_a11y`)
                  : t(`${ns}.collapse_how_it_works_a11y`),
              ),
              isCollapsed
                ? `${ns}.expand_how_it_works_a11y`
                : `${ns}.collapse_how_it_works_a11y`,
            );
            setIsCollapsed((current) => !current);
          }}
          accessibilityRole="button"
          accessibilityLabel={
            isCollapsed
              ? t(`${ns}.expand_how_it_works_a11y`)
              : t(`${ns}.collapse_how_it_works_a11y`)
          }
          style={{ flex: 1 }}
        >
          <SectionTitle icon={Egg}>
            {t("homepage.egg_freezing_biology_carousel.how_it_works")}
          </SectionTitle>
        </Pressable>

        <Pressable
          onPress={handleStatusPress}
          accessibilityRole="button"
          accessibilityLabel={
            isMarkedRead
              ? isCollapsed
                ? t(`${ns}.show_completed_a11y`)
                : t(`${ns}.collapse_completed_a11y`)
              : t(`${ns}.mark_as_read_a11y`)
          }
          style={({ pressed }) => ({
            flexDirection: "row",
            alignItems: "center",
            gap: 5,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: semanticColors.action.quiet.border,
            backgroundColor: pressed
              ? semanticColors.action.quiet.pressedBackground
              : semanticColors.action.quiet.background,
            paddingHorizontal: 9,
            paddingVertical: 5,
          })}
        >
          {isMarkedRead ? (
            <CheckCircle2
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          ) : null}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 13,
              color: EMBER.colors.textLight,
              fontWeight: "700",
              letterSpacing: 0.3,
              textTransform: "uppercase",
            }}
          >
            {isMarkedRead ? t(`${ns}.read`) : t(`${ns}.mark_read`)}
          </Text>
          {isCollapsed ? (
            <ChevronDown
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          ) : (
            <ChevronUp
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.8}
            />
          )}
        </Pressable>
      </View>

      {!isCollapsed ? (
        <DeepBiologyCarouselContent
          userAge={userAge}
          onAction={onAction}
          onOpenSlide={setSelectedSlide}
        />
      ) : null}

      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </View>
  );
}

export function EggFreezingBiologyStepSectionCard({
  userAge,
  onAction,
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const [selectedSlide, setSelectedSlide] = useState<DeepBiologySlide | null>(
    null,
  );

  return (
    <>
      <TaskSectionCard
        id="precycle-egg-freezing.understand"
        stepLabel={t(`${ns}.step_1_understand`)}
        completedStepLabel={t(`${ns}.step_1_complete`)}
        title={t("homepage.egg_freezing_biology_carousel.how_it_works")}
        subtitle={t(`${ns}.section_subtitle`)}
        collapsedPreviewHeight={68}
      >
        <DeepBiologyCarouselContent
          userAge={userAge}
          onAction={onAction}
          onOpenSlide={setSelectedSlide}
        />
      </TaskSectionCard>

      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </>
  );
}

export function EggFreezingBiologyInlineStepSectionCard({
  userAge,
  onAction,
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const [selectedSlide, setSelectedSlide] = useState<DeepBiologySlide | null>(
    null,
  );

  return (
    <>
      <InlineTaskSectionCard
        id="precycle-egg-freezing.understand.inline"
        stepLabel={t(`${ns}.step_1_understand`)}
        title={t("homepage.egg_freezing_biology_carousel.how_it_works")}
        subtitle={t(`${ns}.section_subtitle`)}
        titleIcon={<OrganicMiniIcon kind="process" size={34} />}
        collapsedPreviewHeight={68}
      >
        <DeepBiologyCarouselContent
          userAge={userAge}
          onAction={onAction}
          onOpenSlide={setSelectedSlide}
        />
      </InlineTaskSectionCard>

      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </>
  );
}

export function EggFreezingArchivedBiologyCarouselVariants({
  userAge,
  onAction,
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const [selectedSlide, setSelectedSlide] = useState<DeepBiologySlide | null>(
    null,
  );

  return (
    <>
      <View style={{ gap: 12 }}>
        <TimelineSubtaskCard
          id="archive.precycle-egg-freezing.understand.timeline.how-it-works-organic"
          title={t(`${ns}.archive_organic_title`)}
          subtitle={t(`${ns}.archive_organic_subtitle`)}
          titleIcon={<OrganicMiniIcon kind="process" size={34} />}
          collapsedPreviewHeight={68}
        >
          <DeepBiologyCarouselContent
            userAge={userAge}
            onAction={onAction}
            onOpenSlide={setSelectedSlide}
            showEstimateBridge={false}
            illustrationVariant="organic"
          />
        </TimelineSubtaskCard>

        <TimelineSubtaskCard
          id="archive.precycle-egg-freezing.understand.timeline.how-it-works-flat"
          title={t(`${ns}.archive_flat_title`)}
          subtitle={t(`${ns}.archive_flat_subtitle`)}
          titleIcon={<OrganicMiniIcon kind="process" size={34} />}
          collapsedPreviewHeight={68}
        >
          <DeepBiologyCarouselContent
            userAge={userAge}
            onAction={onAction}
            onOpenSlide={setSelectedSlide}
            showEstimateBridge={false}
          />
        </TimelineSubtaskCard>
      </View>

      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </>
  );
}

export function EggFreezingBiologyTimelineStepSectionCard({
  userAge,
  onAction,
  showRail = true,
  viewerIsSupporting = false,
}: {
  userAge?: number;
  onAction: (action: PlanAction) => void;
  showRail?: boolean;
  /** A partner asks how many eggs a cycle may give, not how many they may get. */
  viewerIsSupporting?: boolean;
}) {
  const t = useTranslations();
  const ns = "homepage.egg_freezing_biology_deep_carousel";
  const [selectedSlide, setSelectedSlide] = useState<DeepBiologySlide | null>(
    null,
  );

  return (
    <>
      <StepperSection
        id="precycle-egg-freezing.understand.timeline"
        stepLabel={t(`${ns}.step_1_understand`)}
        completionStepId={getJourneyStepCompletionId(
          "egg-freezing-solo",
          "learn",
        )}
        showRail={showRail}
      >
        <TimelineSubtaskCard
          id="precycle-egg-freezing.understand.timeline.how-it-works-organic-explanatory"
          title={t("homepage.egg_freezing_biology_carousel.how_it_works")}
          subtitle={t(`${ns}.timeline_how_it_works_subtitle`)}
          titleIcon={<OrganicMiniIcon kind="process" size={34} />}
          collapsedPreviewHeight={68}
        >
          <DeepBiologyCarouselContent
            userAge={userAge}
            onAction={onAction}
            onOpenSlide={setSelectedSlide}
            showEstimateBridge={false}
            illustrationVariant="organic-explanatory"
          />
        </TimelineSubtaskCard>

        <TimelineSubtaskCard
          id="precycle-egg-freezing.understand.timeline.egg-estimate"
          title={
            viewerIsSupporting
              ? t(`${ns}.supporting.how_many_eggs_may_one_cycle_give`)
              : t(`${ns}.how_many_eggs_question`)
          }
          subtitle={t(`${ns}.egg_estimate_subtitle`)}
          titleIcon={<OrganicMiniIcon kind="eggs" size={34} />}
          collapsedPreviewHeight={68}
        >
          <OutcomeEstimateFunnelSection userAge={userAge} onAction={onAction} />
        </TimelineSubtaskCard>

        <CoverageActionRow onAction={onAction} />

        <TimelineSubtaskCard
          id="precycle-egg-freezing.understand.timeline.improve-odds"
          title={t(`${ns}.optimization_protocol_title`)}
          subtitle={t(`${ns}.optimization_protocol_subtitle`)}
          titleIcon={<OrganicMiniIcon kind="optimization" size={34} />}
          collapsedPreviewHeight={68}
        >
          <OptimizationOddsCarousel onAction={onAction} />
        </TimelineSubtaskCard>
      </StepperSection>

      <DetailModal
        slide={selectedSlide}
        onClose={() => setSelectedSlide(null)}
      />
    </>
  );
}
